<template>
  <div :class="$style.container">
    <!-- 통계 탭 네비게이션 -->
    <StatsTabs />

    <!-- 로딩 상태 -->
    <div v-if="loading">
      <LoadingScreen loading-text="광고 데이터를 불러오는 중..." />
    </div>

    <!-- 에러 상태 -->
    <div v-else-if="error" :class="$style.error">
      <p>{{ error }}</p>
      <button @click="fetchAds" :class="$style.retryButton">다시 시도</button>
    </div>

    <!-- 광고 관리 화면 -->
    <div v-else>
      <div :class="$style.header">
        <div :class="$style.titleSection">
          <h1 :class="$style.title">📢 광고 배너 관리</h1>
          <p :class="$style.subtitle">메인 배너와 상세 페이지 광고를 관리합니다</p>
        </div>
      </div>

      <!-- 광고 타입 선택 탭 -->
      <div :class="$style.adTypeTabs">
        <button :class="[$style.adTypeButton, { [$style.activeType]: selectedAdType === 'main' }]"
          @click="selectedAdType = 'main'">
          <span :class="$style.typeIcon">🏠</span>
          <span>메인 배너</span>
          <span :class="$style.badge">{{ mainBannerCount }}</span>
        </button>
        <button :class="[$style.adTypeButton, { [$style.activeType]: selectedAdType === 'detail' }]"
          @click="selectedAdType = 'detail'">
          <span :class="$style.typeIcon">📄</span>
          <span>상세 페이지 광고</span>
          <span :class="$style.badge">{{ detailAdCount }}</span>
        </button>
        <button :class="[$style.adTypeButton, { [$style.activeType]: selectedAdType === 'live' }]"
          @click="selectedAdType = 'live'">
          <span :class="$style.typeIcon">🎵</span>
          <span>라이브 배너</span>
          <span :class="$style.badge">{{ liveBannerCount }}</span>
        </button>
      </div>

      <!-- 메인 배너 관리 -->
      <div v-if="selectedAdType === 'main'" :class="$style.adSection">
        <div :class="$style.sectionHeader">
          <div :class="$style.sectionHeaderContent">
            <div>
              <h2 :class="$style.sectionTitle">메인 배너 관리</h2>
              <p :class="$style.sectionDescription">메인 페이지 상단에 노출되는 배너입니다</p>
            </div>
            <button @click="openAddBannerModal" :class="$style.addButton">➕ 배너 추가</button>
          </div>
        </div>

        <div :class="$style.adList">
          <div v-for="(ad, index) in allBanners" :key="ad.id" :class="$style.adCard">
            <div :class="$style.adIndex">{{ index + 1 }}</div>
            <div :class="$style.adPreview">
              <img v-if="ad.bannerUrl" :src="ad.bannerUrl" :alt="ad.title" :class="$style.adImage" />
              <div v-else :class="$style.noImage">이미지 없음</div>
            </div>
            <div :class="$style.adInfo">
              <div :class="$style.adTitleRow">
                <h3 :class="$style.adTitle">{{ ad.title }}</h3>
                <span :class="$style.bannerSource">
                  {{ ad.source === "live" ? "🎵 라이브" : "📢 커스텀" }}
                </span>
              </div>
              <div :class="$style.adDetails">
                <span :class="$style.adDate">{{ ad.date }}</span>
                <span v-if="ad.place" :class="$style.adPlace">{{ ad.place }}</span>
              </div>
              <div v-if="ad.link" :class="$style.adLink">
                <span>🔗</span>
                <a :href="ad.link" target="_blank">{{ ad.link }}</a>
              </div>
              <div :class="$style.adMeta">
                <span :class="[
                  $style.adStatus,
                  ad.active ? $style.statusActive : $style.statusInactive,
                ]">
                  {{ ad.active ? "노출 중" : "비활성" }}
                </span>
                <span :class="$style.impressionCount"> 👁️ 노출 {{ ad.impressionCount || 0 }}회 </span>
                <span :class="$style.clickCount"> 👆 클릭 {{ ad.clickCount || 0 }}회 </span>
                <span v-if="(ad.impressionCount || 0) > 0" :class="$style.ctr">
                  📊 CTR {{ calculateCTR(ad) }}%
                </span>
              </div>
              <!-- 날짜별 클릭 통계 표시 -->
              <div v-if="hasClickData(ad)" :class="$style.clickStats">
                <button @click="toggleClickStats(ad.id)" :class="$style.statsToggle">
                  {{ ad.showStats ? "📊 통계 접기" : "📊 날짜별 통계 보기" }}
                </button>
                <div v-if="ad.showStats" :class="$style.statsContent">
                  <div :class="$style.statsHeader">
                    <span>날짜별 노출/클릭 통계</span>
                  </div>
                  <div :class="$style.monthFilter">
                    <button v-for="month in getAvailableMonths(ad)" :key="month" @click="selectMonth(ad.id, month)"
                      :class="[
                        $style.monthButton,
                        {
                          [$style.activeMonth]:
                            ad.selectedMonth === month || (!ad.selectedMonth && month === 'all'),
                        },
                      ]">
                      {{ formatMonthLabel(month) }}
                    </button>
                  </div>
                  <div :class="$style.chartWrapper">
                    <Chart type="bar" :data="getChartData(ad)" :options="chartOptions" :class="$style.clickChart" />
                  </div>
                </div>
              </div>
            </div>
            <div :class="$style.adActions">
              <div :class="$style.priorityControls">
                <button @click="changePriority(ad, 1)" :class="[$style.priorityButton, $style.priorityUp]"
                  title="우선순위 올리기" :disabled="ad.updating">
                  ⬆
                </button>
                <button @click="changePriority(ad, -1)" :class="[$style.priorityButton, $style.priorityDown]"
                  title="우선순위 내리기" :disabled="ad.updating">
                  ⬇
                </button>
              </div>
              <button @click="toggleBannerStatus(ad)" :class="[
                $style.actionButton,
                ad.active ? $style.deactivateButton : $style.activateButton,
              ]" :disabled="ad.updating">
                {{ ad.updating ? "처리 중..." : ad.active ? "비활성화" : "활성화" }}
              </button>
              <button @click="openReportModal(ad)" :class="[$style.actionButton, $style.reportButton]">
                📊 통계 보고서
              </button>
              <button @click="openSimpleReportModal(ad)" :class="[$style.actionButton, $style.simpleReportButton]">
                📋 간이 보고서
              </button>
              <button @click="openInvoiceModal(ad)" :class="[$style.actionButton, $style.invoiceButton]">
                🧾 Invoice 발행
              </button>
              <button v-if="ad.source === 'live'" @click="goToLiveDetail(ad.id)"
                :class="[$style.actionButton, $style.viewButton]">
                상세보기
              </button>
              <button v-else @click="editBanner(ad)" :class="[$style.actionButton, $style.editButton]">
                수정
              </button>
              <button v-if="ad.source === 'custom'" @click="deleteBanner(ad)"
                :class="[$style.actionButton, $style.deleteButton]" :disabled="ad.deleting">
                {{ ad.deleting ? "삭제 중..." : "삭제" }}
              </button>
            </div>
          </div>

          <div v-if="allBanners.length === 0" :class="$style.emptyState">
            <p>등록된 배너가 없습니다.</p>
          </div>
        </div>
      </div>

      <!-- 상세 페이지 광고 관리 -->
      <div v-if="selectedAdType === 'detail'" :class="$style.adSection">
        <div :class="$style.sectionHeader">
          <div :class="$style.sectionHeaderContent">
            <div>
              <h2 :class="$style.sectionTitle">상세 페이지 광고 관리</h2>
              <p :class="$style.sectionDescription">
                라이브 상세 페이지와 기타 페이지에 노출되는 광고입니다
              </p>
            </div>
            <button @click="openAddBannerModal" :class="$style.addButton">➕ 광고 추가</button>
          </div>
        </div>

        <div :class="$style.adList">
          <div v-for="(ad, index) in allBanners" :key="ad.id" :class="$style.adCard">
            <div :class="$style.adIndex">{{ index + 1 }}</div>
            <div :class="$style.adPreview">
              <img v-if="ad.bannerUrl" :src="ad.bannerUrl" :alt="ad.title" :class="$style.adImage" />
              <div v-else :class="$style.noImage">이미지 없음</div>
            </div>
            <div :class="$style.adInfo">
              <div :class="$style.adTitleRow">
                <h3 :class="$style.adTitle">{{ ad.title }}</h3>
                <span :class="$style.bannerSource">
                  {{ ad.source === "live" ? "🎵 라이브" : "📢 커스텀" }}
                </span>
              </div>
              <div :class="$style.adDetails">
                <span :class="$style.adDate">{{ ad.date }}</span>
                <span v-if="ad.place" :class="$style.adPlace">{{ ad.place }}</span>
              </div>
              <div v-if="ad.link" :class="$style.adLink">
                <span>🔗</span>
                <a :href="ad.link" target="_blank">{{ ad.link }}</a>
              </div>
              <div :class="$style.adMeta">
                <span :class="[
                  $style.adStatus,
                  ad.active ? $style.statusActive : $style.statusInactive,
                ]">
                  {{ ad.active ? "노출 중" : "비활성" }}
                </span>
                <span :class="$style.priorityBadge"> 🎯 우선순위 {{ ad.priority || 0 }} </span>
                <span :class="$style.impressionCount"> 👁️ 노출 {{ ad.impressionCount || 0 }}회 </span>
                <span :class="$style.clickCount"> 👆 클릭 {{ ad.clickCount || 0 }}회 </span>
                <span v-if="(ad.impressionCount || 0) > 0" :class="$style.ctr">
                  📊 CTR {{ calculateCTR(ad) }}%
                </span>
              </div>
              <!-- 광고 기간 표시 (상세 페이지 광고인 경우) -->
              <div v-if="ad.startDateTime || ad.endDateTime" :class="$style.adPeriod">
                <div v-if="ad.startDateTime" :class="$style.periodItem">
                  <span :class="$style.periodLabel">시작:</span>
                  <span>{{ formatDateTime(ad.startDateTime) }}</span>
                </div>
                <div v-if="ad.endDateTime" :class="$style.periodItem">
                  <span :class="$style.periodLabel">종료:</span>
                  <span>{{ formatDateTime(ad.endDateTime) }}</span>
                </div>
                <div :class="$style.periodStatus">
                  <span :class="[
                    $style.periodBadge,
                    isAdPeriodActive(ad) ? $style.periodActive : $style.periodInactive,
                  ]">
                    {{ getAdPeriodStatus(ad) }}
                  </span>
                </div>
              </div>
              <!-- 날짜별 클릭 통계 표시 -->
              <div v-if="hasClickData(ad)" :class="$style.clickStats">
                <button @click="toggleClickStats(ad.id)" :class="$style.statsToggle">
                  {{ ad.showStats ? "📊 통계 접기" : "📊 날짜별 통계 보기" }}
                </button>
                <div v-if="ad.showStats" :class="$style.statsContent">
                  <div :class="$style.statsHeader">
                    <span>날짜별 노출/클릭 통계</span>
                  </div>
                  <div :class="$style.monthFilter">
                    <button v-for="month in getAvailableMonths(ad)" :key="month" @click="selectMonth(ad.id, month)"
                      :class="[
                        $style.monthButton,
                        {
                          [$style.activeMonth]:
                            ad.selectedMonth === month || (!ad.selectedMonth && month === 'all'),
                        },
                      ]">
                      {{ formatMonthLabel(month) }}
                    </button>
                  </div>
                  <div :class="$style.chartWrapper">
                    <Chart type="bar" :data="getChartData(ad)" :options="chartOptions" :class="$style.clickChart" />
                  </div>
                </div>
              </div>
            </div>
            <div :class="$style.adActions">
              <div :class="$style.priorityControls">
                <button @click="changePriority(ad, 1)" :class="[$style.priorityButton, $style.priorityUp]"
                  title="우선순위 올리기" :disabled="ad.updating">
                  ⬆
                </button>
                <button @click="changePriority(ad, -1)" :class="[$style.priorityButton, $style.priorityDown]"
                  title="우선순위 내리기" :disabled="ad.updating">
                  ⬇
                </button>
              </div>
              <button @click="toggleBannerStatus(ad)" :class="[
                $style.actionButton,
                ad.active ? $style.deactivateButton : $style.activateButton,
              ]" :disabled="ad.updating">
                {{ ad.updating ? "처리 중..." : ad.active ? "비활성화" : "활성화" }}
              </button>
              <!-- 통계 보고서 버튼 -->
              <button @click="openReportModal(ad)" :class="[$style.actionButton, $style.reportButton]">
                📊 통계 보고서
              </button>
              <button @click="openSimpleReportModal(ad)" :class="[$style.actionButton, $style.simpleReportButton]">
                📋 간이 보고서
              </button>
              <button @click="openInvoiceModal(ad)" :class="[$style.actionButton, $style.invoiceButton]">
                🧾 Invoice 발행
              </button>
              <!-- 라이브 배너는 상세보기만, 커스텀 배너는 수정/삭제 가능 -->
              <button v-if="ad.source === 'live'" @click="goToLiveDetail(ad.id)"
                :class="[$style.actionButton, $style.viewButton]">
                상세보기
              </button>
              <template v-else>
                <button @click="editBanner(ad)" :class="[$style.actionButton, $style.editButton]">
                  수정
                </button>
                <button @click="deleteBanner(ad)" :class="[$style.actionButton, $style.deleteButton]"
                  :disabled="ad.deleting">
                  {{ ad.deleting ? "삭제 중..." : "삭제" }}
                </button>
              </template>
            </div>
          </div>

          <div v-if="allBanners.length === 0" :class="$style.emptyState">
            <p>등록된 상세 페이지 광고가 없습니다.</p>
          </div>
        </div>
      </div>

      <!-- 라이브 배너 관리 -->
      <div v-if="selectedAdType === 'live'" :class="$style.adSection">
        <div :class="$style.sectionHeader">
          <div :class="$style.sectionHeaderContent">
            <div>
              <h2 :class="$style.sectionTitle">라이브 배너 관리</h2>
              <p :class="$style.sectionDescription">
                메인 페이지 배너 및 상세페이지에 노출되는 라이브 공연들입니다
              </p>
            </div>
          </div>
        </div>

        <div :class="$style.adList">
          <div v-for="(ad, index) in allBanners" :key="ad.id" :class="$style.adCard">
            <div :class="$style.adIndex">{{ index + 1 }}</div>
            <div :class="$style.adPreview">
              <img v-if="ad.bannerUrl" :src="ad.bannerUrl" :alt="ad.title" :class="$style.adImage" />
              <div v-else :class="$style.noImage">이미지 없음</div>
            </div>
            <div :class="$style.adInfo">
              <div :class="$style.adTitleRow">
                <h3 :class="$style.adTitle">{{ ad.title }}</h3>
                <span :class="$style.bannerSource"> 🎵 라이브 </span>
              </div>
              <div :class="$style.adDetails">
                <span :class="$style.adDate">{{ ad.date }}</span>
                <span v-if="ad.place" :class="$style.adPlace">{{ ad.place }}</span>
              </div>
              <div :class="$style.adMeta">
                <span :class="[
                  $style.adStatus,
                  ad.active ? $style.statusActive : $style.statusInactive,
                ]">
                  {{ ad.active ? "노출 중" : "비활성" }}
                </span>
                <span :class="$style.priorityBadge"> 🎯 우선순위 {{ ad.priority || 0 }} </span>
                <span :class="$style.impressionCount"> 👁️ 노출 {{ ad.impressionCount || 0 }}회 </span>
                <span :class="$style.clickCount"> 👆 클릭 {{ ad.clickCount || 0 }}회 </span>
                <span v-if="(ad.impressionCount || 0) > 0" :class="$style.ctr">
                  📊 CTR {{ calculateCTR(ad) }}%
                </span>
              </div>
              <!-- 날짜별 클릭 통계 표시 -->
              <div v-if="hasClickData(ad)" :class="$style.clickStats">
                <button @click="toggleClickStats(ad.id)" :class="$style.statsToggle">
                  {{ ad.showStats ? "📊 통계 접기" : "📊 날짜별 통계 보기" }}
                </button>
                <div v-if="ad.showStats" :class="$style.statsContent">
                  <div :class="$style.statsHeader">
                    <span>날짜별 노출/클릭 통계</span>
                  </div>
                  <div :class="$style.monthFilter">
                    <button v-for="month in getAvailableMonths(ad)" :key="month" @click="selectMonth(ad.id, month)"
                      :class="[
                        $style.monthButton,
                        {
                          [$style.activeMonth]:
                            ad.selectedMonth === month || (!ad.selectedMonth && month === 'all'),
                        },
                      ]">
                      {{ formatMonthLabel(month) }}
                    </button>
                  </div>
                  <div :class="$style.chartWrapper">
                    <Chart type="bar" :data="getChartData(ad)" :options="chartOptions" :class="$style.clickChart" />
                  </div>
                </div>
              </div>
            </div>
            <div :class="$style.adActions">
              <button @click="openReportModal(ad)" :class="[$style.actionButton, $style.reportButton]">
                📊 통계 보고서
              </button>
              <button @click="openSimpleReportModal(ad)" :class="[$style.actionButton, $style.simpleReportButton]">
                📋 간이 보고서
              </button>
              <button @click="openInvoiceModal(ad)" :class="[$style.actionButton, $style.invoiceButton]">
                🧾 Invoice 발행
              </button>
              <button v-if="ad.active" @click="toggleBannerStatus(ad)"
                :class="[$style.actionButton, $style.deactivateButton]" :disabled="ad.updating">
                {{ ad.updating ? "처리 중..." : "비활성화" }}
              </button>
              <button @click="goToLiveDetail(ad.id)" :class="[$style.actionButton, $style.viewButton]">
                상세보기
              </button>
            </div>
          </div>

          <div v-if="allBanners.length === 0" :class="$style.emptyState">
            <p>등록된 라이브 배너가 없습니다.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 통계 보고서 모달 (A4 문서 스타일) -->
    <Dialog v-model:visible="showReportModal" :modal="true" :closable="true" :showHeader="false"
      :class="$style.reportModal" :style="{ width: '90%', maxWidth: '900px' }">
      <div v-if="selectedAdForReport" :class="$style.a4Document">
        <!-- 문서 헤더 -->
        <div :class="$style.documentHeader">
          <div :class="$style.documentLogo">LINKOOK LIVE</div>
          <div :class="$style.documentTitle">광고 성과 보고서</div>
          <div :class="$style.documentMeta">
            <span>발행일: {{ getCurrentDate() }}</span>
            <span :class="$style.documentDivider">|</span>
            <span>보고서 ID: RPT-{{ selectedAdForReport.id.substring(0, 8).toUpperCase() }}</span>
          </div>
        </div>

        <!-- 기간 선택 UI -->
        <div :class="$style.periodSelector">
          <div :class="$style.periodLabel">📅 통계 기간 선택</div>
          <div :class="$style.periodButtons">
            <button :class="[$style.periodBtn, { [$style.active]: reportPeriodType === 'all' }]"
              @click="setReportPeriod('all')">전체</button>
            <button :class="[$style.periodBtn, { [$style.active]: reportPeriodType === 'week' }]"
              @click="setReportPeriod('week')">최근 7일</button>
            <button :class="[$style.periodBtn, { [$style.active]: reportPeriodType === 'month' }]"
              @click="setReportPeriod('month')">최근 1개월</button>
            <button :class="[$style.periodBtn, { [$style.active]: reportPeriodType === '3months' }]"
              @click="setReportPeriod('3months')">최근 3개월</button>
            <button :class="[$style.periodBtn, { [$style.active]: reportPeriodType === 'custom' }]"
              @click="setReportPeriod('custom')">직접 선택</button>
          </div>
          <div v-if="reportPeriodType === 'custom'" :class="$style.customPeriod">
            <input type="date" v-model="reportStartDate" :class="$style.dateInput" />
            <span :class="$style.dateSeparator">~</span>
            <input type="date" v-model="reportEndDate" :class="$style.dateInput" />
          </div>
          <div v-if="reportPeriodType !== 'all'" :class="$style.periodInfo">
            📊 {{ getReportPeriodText() }}
          </div>
        </div>

        <!-- 문서 본문 -->
        <div :class="$style.documentBody">
          <!-- 1. 광고 개요 섹션 -->
          <section :class="$style.docSection">
            <div :class="$style.sectionNumber">01</div>
            <h2 :class="$style.docSectionTitle">광고 개요</h2>
            <div :class="$style.docInfoBox">
              <div :class="$style.docInfoRow">
                <div :class="$style.docInfoCell">
                  <span :class="$style.docLabel">광고명</span>
                  <span :class="$style.docValue">{{ selectedAdForReport.title }}</span>
                </div>
                <div :class="$style.docInfoCell">
                  <span :class="$style.docLabel">광고일</span>
                  <span :class="$style.docValue">{{ selectedAdForReport.date }}</span>
                </div>
              </div>
              <div :class="$style.docInfoRow">
                <div v-if="selectedAdForReport.place" :class="$style.docInfoCell">
                  <span :class="$style.docLabel">장소</span>
                  <span :class="$style.docValue">{{ selectedAdForReport.place }}</span>
                </div>
                <div :class="$style.docInfoCell">
                  <span :class="$style.docLabel">광고 유형</span>
                  <span :class="$style.docValue">
                    {{ selectedAdForReport.source === "live" ? "라이브 연동" : "커스텀 배너" }}
                  </span>
                </div>
              </div>
              <div :class="$style.docInfoRow">
                <div :class="$style.docInfoCell">
                  <span :class="$style.docLabel">현재 상태</span>
                  <span
                    :class="[$style.docValue, $style.docStatus, selectedAdForReport.active ? $style.docStatusActive : $style.docStatusInactive]">
                    {{ selectedAdForReport.active ? "● 노출 중" : "○ 비활성" }}
                  </span>
                </div>
                <div :class="$style.docInfoCell">
                  <span :class="$style.docLabel">우선순위</span>
                  <span :class="$style.docValue">{{ selectedAdForReport.priority || 0 }}순위</span>
                </div>
              </div>
              <!-- 광고 기간 정보 -->
              <div v-if="selectedAdForReport.startDateTime || selectedAdForReport.endDateTime"
                :class="$style.docInfoRow">
                <div v-if="selectedAdForReport.startDateTime" :class="$style.docInfoCell">
                  <span :class="$style.docLabel">광고 시작</span>
                  <span :class="$style.docValue">{{ formatDateTime(selectedAdForReport.startDateTime) }}</span>
                </div>
                <div v-if="selectedAdForReport.endDateTime" :class="$style.docInfoCell">
                  <span :class="$style.docLabel">광고 종료</span>
                  <span :class="$style.docValue">{{ formatDateTime(selectedAdForReport.endDateTime) }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- 2. 핵심 성과 지표 -->
          <section :class="$style.docSection">
            <div :class="$style.sectionNumber">02</div>
            <h2 :class="$style.docSectionTitle">핵심 성과 지표 (KPI)</h2>
            <div :class="$style.kpiGrid">
              <div :class="$style.kpiCard">
                <div :class="$style.kpiIcon">👁️</div>
                <div :class="$style.kpiContent">
                  <div :class="$style.kpiLabel">총 노출 수</div>
                  <div :class="$style.kpiValue">{{ getTotalViews().toLocaleString() }}</div>
                  <div :class="$style.kpiUnit">회</div>
                </div>
              </div>
              <div :class="$style.kpiCard">
                <div :class="$style.kpiIcon">👆</div>
                <div :class="$style.kpiContent">
                  <div :class="$style.kpiLabel">총 클릭 수</div>
                  <div :class="$style.kpiValue">{{ getTotalClicks().toLocaleString() }}</div>
                  <div :class="$style.kpiUnit">회</div>
                </div>
              </div>
              <div :class="[$style.kpiCard, $style.kpiHighlight]">
                <div :class="$style.kpiIcon">📈</div>
                <div :class="$style.kpiContent">
                  <div :class="$style.kpiLabel">클릭률 (CTR)</div>
                  <div :class="$style.kpiValue">{{ getFilteredCTR() }}</div>
                  <div :class="$style.kpiUnit">%</div>
                </div>
              </div>
            </div>
          </section>

          <!-- 3. 상세 통계 분석 -->
          <section :class="$style.docSection">
            <div :class="$style.sectionNumber">03</div>
            <h2 :class="$style.docSectionTitle">상세 통계 분석</h2>

            <div :class="$style.statsCompare">
              <!-- 노출 통계 -->
              <div :class="$style.statsBox">
                <h4 :class="$style.statsBoxTitle">
                  <span :class="$style.statsBoxIcon">👁️</span> 노출 통계
                </h4>
                <div :class="$style.statsItems">
                  <div :class="$style.statsItem">
                    <span :class="$style.statsItemLabel">통계 기간</span>
                    <span :class="$style.statsItemValue">{{ hasViewData() ? getViewStatsPeriod() : '-' }}</span>
                  </div>
                  <div :class="$style.statsItem">
                    <span :class="$style.statsItemLabel">평균 일일 노출</span>
                    <span :class="$style.statsItemValue">{{ hasViewData() ? getAverageDailyViews() : 0 }}회</span>
                  </div>
                  <div :class="$style.statsItem">
                    <span :class="$style.statsItemLabel">최대 일일 노출</span>
                    <span :class="$style.statsItemValue">{{ hasViewData() ? getMaxDailyViews() : 0 }}회</span>
                  </div>
                </div>
              </div>

              <!-- 클릭 통계 -->
              <div :class="$style.statsBox">
                <h4 :class="$style.statsBoxTitle">
                  <span :class="$style.statsBoxIcon">👆</span> 클릭 통계
                </h4>
                <div :class="$style.statsItems">
                  <div :class="$style.statsItem">
                    <span :class="$style.statsItemLabel">통계 기간</span>
                    <span :class="$style.statsItemValue">{{ hasClickData(selectedAdForReport) ?
                      getStatsPeriod(selectedAdForReport) : '-' }}</span>
                  </div>
                  <div :class="$style.statsItem">
                    <span :class="$style.statsItemLabel">평균 일일 클릭</span>
                    <span :class="$style.statsItemValue">{{ getAverageDailyClicks(selectedAdForReport) }}회</span>
                  </div>
                  <div :class="$style.statsItem">
                    <span :class="$style.statsItemLabel">최대 일일 클릭</span>
                    <span :class="$style.statsItemValue">{{ getMaxDailyClicks(selectedAdForReport) }}회</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- 4. 노출 추이 차트 -->
          <section v-if="hasViewData()" :class="$style.docSection">
            <div :class="$style.sectionNumber">04</div>
            <h2 :class="$style.docSectionTitle">일별 노출 추이</h2>
            <div :class="$style.docChartWrapper">
              <Chart type="bar" :data="getViewChartData()" :options="reportViewChartOptions" :class="$style.docChart" />
            </div>
          </section>

          <!-- 5. 클릭 추이 차트 -->
          <section v-if="hasClickData(selectedAdForReport)" :class="$style.docSection">
            <div :class="$style.sectionNumber">{{ hasViewData() ? '05' : '04' }}</div>
            <h2 :class="$style.docSectionTitle">일별 클릭 추이</h2>
            <div :class="$style.docChartWrapper">
              <Chart type="bar" :data="getChartData(selectedAdForReport)" :options="reportChartOptions"
                :class="$style.docChart" />
            </div>
          </section>

          <!-- 6. 일별 상세 데이터 -->
          <section v-if="hasClickData(selectedAdForReport)" :class="$style.docSection">
            <div :class="$style.sectionNumber">{{ hasViewData() ? '06' : '05' }}</div>
            <h2 :class="$style.docSectionTitle">일별 상세 데이터</h2>
            <div :class="$style.docTableWrapper">
              <table :class="$style.docTable">
                <thead>
                  <tr>
                    <th>날짜</th>
                    <th>노출 수</th>
                    <th>클릭 수</th>
                    <th>클릭률</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="[date, impressionCount, clickCount] in getSortedClickData(selectedAdForReport)"
                    :key="date">
                    <td>{{ formatDateForReport(date) }}</td>
                    <td>{{ impressionCount.toLocaleString() }}회</td>
                    <td>{{ clickCount.toLocaleString() }}회</td>
                    <td :class="$style.ctrHighlight">{{ getDailyCTR(selectedAdForReport, date) }}%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- 통계가 없는 경우 -->
          <section v-if="!hasViewData() && !hasClickData(selectedAdForReport)" :class="$style.docSection">
            <div :class="$style.docNoData">
              <div :class="$style.docNoDataIcon">📊</div>
              <p :class="$style.docNoDataText">아직 수집된 통계 데이터가 없습니다.</p>
              <p :class="$style.docNoDataSub">광고가 노출되면 데이터가 자동으로 수집됩니다.</p>
            </div>
          </section>
        </div>

        <!-- 문서 푸터 -->
        <div :class="$style.documentFooter">
          <div :class="$style.footerLine"></div>
          <div :class="$style.footerContent">
            <span>© {{ new Date().getFullYear() }} Linkook Live. All rights reserved.</span>
            <span :class="$style.footerPage">Page 1 of 1</span>
          </div>
        </div>

        <!-- 닫기 버튼 -->
        <button @click="showReportModal = false" :class="$style.docCloseButton">
          ✕ 닫기
        </button>
      </div>
    </Dialog>

    <!-- 간이 광고 성과 보고서 모달 -->
    <Dialog v-model:visible="showSimpleReportModal" :modal="true" :closable="true" :showHeader="false"
      :class="$style.simpleReportModal" :style="{ width: '90%', maxWidth: '600px' }">
      <div v-if="selectedAdForSimpleReport" :class="$style.simpleReport">
        <!-- 간이 보고서 헤더 -->
        <div :class="$style.simpleHeader">
          <div :class="$style.simpleHeaderBadge">SIMPLE REPORT</div>
          <h2 :class="$style.simpleTitle">광고 성과 보고서</h2>
          <p :class="$style.simpleSubtitle">{{ getSimple3MonthPeriodText() }}</p>
        </div>

        <!-- 광고 정보 -->
        <div :class="$style.simpleAdInfo">
          <div :class="$style.simpleAdName">{{ selectedAdForSimpleReport.title }}</div>
          <div :class="$style.simpleAdMeta">
            <span>{{ selectedAdForSimpleReport.date }}</span>
            <span v-if="selectedAdForSimpleReport.place">· {{ selectedAdForSimpleReport.place }}</span>
          </div>
        </div>

        <!-- 핵심 지표 -->
        <div :class="$style.simpleMetrics">
          <div :class="$style.simpleMetricCard">
            <div :class="$style.simpleMetricIcon">👆</div>
            <div :class="$style.simpleMetricContent">
              <div :class="$style.simpleMetricLabel">3개월 총 클릭</div>
              <div :class="$style.simpleMetricValue">{{ getSimple3MonthClicks().toLocaleString() }}회</div>
            </div>
          </div>
          <div :class="[$style.simpleMetricCard, $style.simpleMetricHighlight]">
            <div :class="$style.simpleMetricIcon">📈</div>
            <div :class="$style.simpleMetricContent">
              <div :class="$style.simpleMetricLabel">추정 클릭률</div>
              <div :class="$style.simpleMetricValue">{{ getSimpleCTR() }}%</div>
            </div>
          </div>
        </div>

        <!-- 참고 자료 섹션 -->
        <div :class="$style.simpleReference">
          <h4 :class="$style.simpleRefTitle">📊 참고 자료</h4>
          <div :class="$style.simpleRefContent">
            <div :class="$style.simpleRefItem">
              <span :class="$style.simpleRefLabel">최근 3개월 일평균 방문수</span>
              <span :class="$style.simpleRefValue">{{ getSimple3MonthAvgVisitors().toLocaleString() }}명</span>
            </div>
            <div :class="$style.simpleRefItem">
              <span :class="$style.simpleRefLabel">최근 3개월 총 노출 수</span>
              <span :class="$style.simpleRefValue">{{ getSimple3MonthTotalImpressions().toLocaleString() }}회</span>
            </div>
            <div :class="$style.simpleRefItem">
              <span :class="$style.simpleRefLabel">최근 3개월 총 방문수</span>
              <span :class="$style.simpleRefValue">{{ getSimple3MonthTotalVisitors().toLocaleString() }}명</span>
            </div>
            <div :class="$style.simpleRefItem">
              <span :class="$style.simpleRefLabel">클릭 데이터 일수</span>
              <span :class="$style.simpleRefValue">{{ getSimple3MonthClickDays() }}일</span>
            </div>
          </div>
          <p :class="$style.simpleRefNote">
            * 추정 클릭률은 (3개월 총 클릭 ÷ 3개월 총 노출수) × 100으로 산출됩니다.
          </p>
        </div>

        <!-- 6개월 성장추이 -->
        <div :class="$style.simpleGrowth">
          <h4 :class="$style.simpleGrowthTitle">📈 링꾹라이브 6개월 성장추이</h4>
          <div :class="$style.simpleGrowthChartWrapper">
            <Chart type="bar" :data="get6MonthChartData()" :options="simpleGrowthChartOptions"
              :class="$style.simpleGrowthChart" />
          </div>
          <div :class="$style.simpleGrowthStats">
            <div :class="$style.growthStatItem">
              <span :class="$style.growthStatLabel">6개월 방문</span>
              <span :class="$style.growthStatValue">{{ get6MonthTotalVisitors().toLocaleString() }}명</span>
            </div>
            <div :class="$style.growthStatItem">
              <span :class="$style.growthStatLabel">월평균 방문</span>
              <span :class="$style.growthStatValue">{{ get6MonthAvgMonthlyVisitors().toLocaleString() }}명</span>
            </div>
            <div :class="$style.growthStatItem">
              <span :class="$style.growthStatLabel">일평균 방문</span>
              <span :class="$style.growthStatValue">{{ get6MonthAvgDailyVisitors().toLocaleString() }}명</span>
            </div>
            <div :class="[$style.growthStatItem, $style.growthStatHighlight]">
              <span :class="$style.growthStatLabel">성장률</span>
              <span :class="$style.growthStatValue">{{ get6MonthGrowthRate() }}</span>
            </div>
          </div>
        </div>

        <!-- 푸터 -->
        <div :class="$style.simpleFooter">
          <span>발행일: {{ getSimpleReportIssueDate() }}</span>
          <div :class="$style.simpleFooterControls">
            <div :class="$style.monthNavigator">
              <button @click="changeSimpleReportMonth(-1)" :class="$style.monthNavBtn">◀ 이전달</button>
              <button @click="changeSimpleReportMonth(1)" :class="$style.monthNavBtn" :disabled="!canGoNextMonth()">다음달
                ▶</button>
            </div>
            <button @click="showSimpleReportModal = false" :class="$style.simpleCloseBtn">닫기</button>
          </div>
        </div>
      </div>
    </Dialog>

    <!-- Invoice 미리보기 모달 -->
    <Dialog v-model:visible="showInvoiceModal" :modal="true" :closable="true" :showHeader="false"
      :class="$style.invoiceModal" :style="{ width: '90%', maxWidth: '760px' }">
      <div v-if="selectedAdForInvoice" :class="$style.invoiceModalContent">
        <div :class="$style.invoicePreviewLabel">Invoice 미리보기</div>
        <div :class="$style.invoicePeriodEditor">
          <div :class="$style.invoicePeriodField">
            <label :class="$style.invoicePeriodLabel">청구 시작일</label>
            <input v-model="invoiceStartDate" type="date" :class="$style.invoicePeriodInput" />
          </div>
          <div :class="$style.invoicePeriodField">
            <label :class="$style.invoicePeriodLabel">청구 종료일</label>
            <input v-model="invoiceEndDate" type="date" :class="$style.invoicePeriodInput" />
          </div>
        </div>
        <div ref="invoicePreview" :class="$style.invoiceDocument">
          <div :class="$style.invoiceHeader">
            <div>
              <div :class="$style.invoiceEyebrow">LINKOOK LIVE</div>
              <h2 :class="$style.invoiceTitle">광고 집행 명세서</h2>
              <p :class="$style.invoiceSubtitle">광고비 청구 및 집행 확인용 Invoice</p>
            </div>
            <div :class="$style.invoiceMeta">
              <div :class="$style.invoiceMetaRow">
                <span :class="$style.invoiceMetaLabel">Invoice No.</span>
                <span :class="$style.invoiceMetaValue">{{ getInvoiceNumber(selectedAdForInvoice) }}</span>
              </div>
              <div :class="$style.invoiceMetaRow">
                <span :class="$style.invoiceMetaLabel">발행일</span>
                <span :class="$style.invoiceMetaValue">{{ getCurrentDate() }}</span>
              </div>
              <div :class="$style.invoiceMetaRow">
                <span :class="$style.invoiceMetaLabel">상태</span>
                <span :class="$style.invoiceMetaValue">
                  {{ selectedAdForInvoice.active ? "발행 완료" : "비활성 광고" }}
                </span>
              </div>
            </div>
          </div>

          <div :class="$style.invoiceSection">
            <div :class="$style.invoiceSectionHeading">공급자 정보</div>
            <div :class="$style.invoiceBusinessTable">
              <div :class="$style.invoiceBusinessRow">
                <div :class="$style.invoiceBusinessCell">
                  <span :class="$style.invoiceBusinessLabel">사업자등록번호</span>
                  <span :class="$style.invoiceBusinessValue">{{ invoiceBusiness.registrationNumber }}</span>
                </div>
                <div :class="$style.invoiceBusinessCell">
                  <span :class="$style.invoiceBusinessLabel">상호(회사명)</span>
                  <span :class="$style.invoiceBusinessValue">{{ invoiceBusiness.companyName }}</span>
                </div>
              </div>
              <div :class="$style.invoiceBusinessRow">
                <div :class="$style.invoiceBusinessCell">
                  <span :class="$style.invoiceBusinessLabel">대표자명</span>
                  <span :class="$style.invoiceBusinessValue">{{ invoiceBusiness.representativeName }}</span>
                </div>
                <div :class="$style.invoiceBusinessCell">
                  <span :class="$style.invoiceBusinessLabel">사업장 주소</span>
                  <span :class="$style.invoiceBusinessValue">{{ invoiceBusiness.address }}</span>
                </div>
              </div>
              <div :class="$style.invoiceBusinessRow">
                <div :class="$style.invoiceBusinessCell">
                  <span :class="$style.invoiceBusinessLabel">업태</span>
                  <span :class="$style.invoiceBusinessValue">{{ invoiceBusiness.businessType }}</span>
                </div>
                <div :class="$style.invoiceBusinessCell">
                  <span :class="$style.invoiceBusinessLabel">종목</span>
                  <span :class="$style.invoiceBusinessValue">{{ invoiceBusiness.businessItem }}</span>
                </div>
              </div>
            </div>
          </div>

          <div :class="$style.invoiceSection">
            <div :class="$style.invoiceSectionHeading">청구 항목</div>
            <div :class="$style.invoiceLineItems">
              <div :class="[$style.invoiceLineRow, $style.invoiceLineHeader]">
                <span>품목</span>
                <span>광고명</span>
                <span>집행 정보</span>
                <span>금액</span>
              </div>
              <div :class="$style.invoiceLineRow">
                <span>{{ getInvoiceItemName(selectedAdForInvoice) }}</span>
                <span>{{ selectedAdForInvoice.title }}</span>
                <span>{{ getInvoiceLineDescription(selectedAdForInvoice) }}</span>
                <span>{{ formatInvoiceCurrency(getInvoiceSupplyAmount()) }}</span>
              </div>
              <div :class="$style.invoiceAmountSummary">
                <div :class="$style.invoiceAmountRow">
                  <span>공급가액</span>
                  <strong>{{ formatInvoiceCurrency(getInvoiceSupplyAmount()) }}</strong>
                </div>
                <div :class="$style.invoiceAmountRow">
                  <span>VAT (10%)</span>
                  <strong>{{ formatInvoiceCurrency(getInvoiceVatAmount()) }}</strong>
                </div>
                <div :class="[$style.invoiceAmountRow, $style.invoiceAmountTotal]">
                  <span>합계 금액</span>
                  <strong>{{ formatInvoiceCurrency(getInvoiceTotalAmount()) }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div :class="$style.invoiceSection">
            <div :class="$style.invoiceSectionHeading">입금 계좌정보</div>
            <div :class="$style.invoiceAccountBox">
              <div :class="$style.invoiceAccountRow">
                <span :class="$style.invoiceAccountLabel">은행명</span>
                <span :class="$style.invoiceAccountValue">{{ invoiceBusiness.bankName }}</span>
              </div>
              <div :class="$style.invoiceAccountRow">
                <span :class="$style.invoiceAccountLabel">예금주</span>
                <span :class="$style.invoiceAccountValue">{{ invoiceBusiness.accountHolder }}</span>
              </div>
              <div :class="$style.invoiceAccountRow">
                <span :class="$style.invoiceAccountLabel">계좌번호</span>
                <span :class="$style.invoiceAccountValue">{{ invoiceBusiness.accountNumber }}</span>
              </div>
            </div>
          </div>

          <div :class="$style.invoiceSection">
            <p :class="$style.invoiceNoteText">
              위 금액을 아래 계좌로 입금 부탁드립니다.
            </p>
          </div>

          <div :class="$style.invoiceFooter">
            <span>공급자: {{ invoiceBusiness.companyName }}</span>
            <span>합계 {{ formatInvoiceCurrency(getInvoiceTotalAmount()) }}</span>
            <span>{{ getInvoiceNumber(selectedAdForInvoice) }}</span>
          </div>
        </div>

        <div :class="$style.invoiceActions">
          <button @click="showInvoiceModal = false" :class="[$style.invoiceActionButton, $style.invoiceCancelButton]">
            닫기
          </button>
          <button @click="saveInvoiceImage" :class="[$style.invoiceActionButton, $style.invoiceSaveButton]"
            :disabled="invoiceSaving">
            {{ invoiceSaving ? "저장 중..." : "이미지 저장" }}
          </button>
        </div>
      </div>
    </Dialog>

    <!-- 배너 추가/수정 모달 -->
    <Dialog v-model:visible="showBannerModal" :header="editingBanner ? '배너 수정' : '배너 추가'" :modal="true" :closable="true"
      :class="$style.bannerModal" :style="{ width: '600px' }">
      <div :class="$style.modalContent">
        <div :class="$style.formGroup">
          <label :class="$style.formLabel">배너 제목 *</label>
          <input v-model="bannerForm.title" type="text" :class="$style.formInput" placeholder="배너 제목을 입력하세요" />
        </div>

        <div :class="$style.formGroup">
          <label :class="$style.formLabel">배너 이미지 *</label>

          <!-- 이미지 미리보기 -->
          <div v-if="imagePreview" :class="$style.imagePreviewContainer">
            <img :src="imagePreview" alt="미리보기" :class="$style.imagePreview" />
            <button type="button" @click="clearImage" :class="$style.clearImageButton">
              ✕ 이미지 제거
            </button>
          </div>

          <!-- 파일 업로드 -->
          <div :class="$style.uploadArea">
            <input ref="fileInput" type="file" accept="image/*" @change="handleImageUpload" :class="$style.fileInput"
              :disabled="uploading" />
            <button type="button" @click="triggerFileInput" :class="$style.uploadButton" :disabled="uploading">
              {{ uploading ? "업로드 중..." : "📁 이미지 업로드" }}
            </button>
            <div v-if="uploading" :class="$style.uploadProgress">
              <div :class="$style.progressBar">
                <div :class="$style.progressFill" :style="{ width: `${uploadProgress}%` }"></div>
              </div>
              <span :class="$style.progressText">{{ uploadProgress }}%</span>
            </div>
          </div>

          <!-- 또는 URL 직접 입력 -->
          <div :class="$style.orDivider">또는</div>
          <input v-model="bannerForm.img" type="text" :class="$style.formInput" placeholder="이미지 URL 또는 파일명을 직접 입력"
            @input="handleManualInput" />
          <small :class="$style.formHint">
            파일을 업로드하거나, Storage의 banner/ 폴더에 있는 파일명 또는 전체 URL을 입력하세요
          </small>
        </div>

        <div :class="$style.formGroup">
          <label :class="$style.formLabel">링크 URL</label>
          <input v-model="bannerForm.link" type="text" :class="$style.formInput" placeholder="클릭 시 이동할 URL (선택사항)" />
        </div>

        <div :class="$style.formRow">
          <div :class="$style.formGroup">
            <label :class="$style.formLabel">날짜 *</label>
            <input v-model="bannerForm.date" type="text" :class="$style.formInput" placeholder="YYYY.MM.DD" />
          </div>

          <div :class="$style.formGroup">
            <label :class="$style.formLabel">장소</label>
            <input v-model="bannerForm.place" type="text" :class="$style.formInput" placeholder="장소 (선택사항)" />
          </div>
        </div>

        <div :class="$style.formGroup">
          <label :class="$style.formLabel">배너 타입 *</label>
          <div :class="$style.radioGroup">
            <label :class="$style.radioLabel">
              <input v-model="bannerForm.type" type="radio" value="main" :class="$style.formRadio" />
              <span :class="$style.radioText">
                <strong>메인 배너</strong> - 홈 화면 상단에 표시
              </span>
            </label>
            <label :class="$style.radioLabel">
              <input v-model="bannerForm.type" type="radio" value="detail" :class="$style.formRadio" />
              <span :class="$style.radioText">
                <strong>상세 페이지 광고</strong> - 라이브 상세 페이지 등에 표시
              </span>
            </label>
          </div>
        </div>

        <!-- 상세 페이지 광고인 경우 광고 기간 설정 -->
        <div v-if="bannerForm.type === 'detail'" :class="$style.adPeriodSection">
          <div :class="$style.periodSectionHeader">
            <h4 :class="$style.periodSectionTitle">📅 광고 노출 기간 설정</h4>
            <p :class="$style.periodSectionDescription">
              광고가 노출될 시작일시와 종료일시를 설정하세요. 설정하지 않으면 항상 노출됩니다.
            </p>
          </div>

          <div :class="$style.formRow">
            <div :class="$style.formGroup">
              <label :class="$style.formLabel">광고 시작일시</label>
              <input v-model="bannerForm.startDateTime" type="datetime-local" :class="$style.formInput" />
              <small :class="$style.formHint"> 시작일시를 설정하지 않으면 즉시 노출됩니다 </small>
            </div>

            <div :class="$style.formGroup">
              <label :class="$style.formLabel">광고 종료일시</label>
              <input v-model="bannerForm.endDateTime" type="datetime-local" :class="$style.formInput" />
              <small :class="$style.formHint"> 종료일시를 설정하지 않으면 계속 노출됩니다 </small>
            </div>
          </div>
        </div>

        <div :class="$style.formGroup">
          <label :class="$style.formLabel">우선순위</label>
          <input v-model.number="bannerForm.priority" type="number" :class="$style.formInput" placeholder="0" min="0"
            step="1" />
          <small :class="$style.formHint"> 숫자가 클수록 먼저 표시됩니다 (기본값: 0) </small>
        </div>

        <div :class="$style.formGroup">
          <label :class="$style.formLabel">
            <input v-model="bannerForm.active" type="checkbox" :class="$style.formCheckbox" />
            활성화 (즉시 노출)
          </label>
        </div>
      </div>

      <template #footer>
        <div :class="$style.modalFooter">
          <button @click="showBannerModal = false" :class="[$style.modalButton, $style.cancelButton]">
            취소
          </button>
          <button @click="saveBanner" :class="[$style.modalButton, $style.saveButton]"
            :disabled="saving || !isFormValid">
            {{ saving ? "저장 중..." : editingBanner ? "수정" : "추가" }}
          </button>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script>
import { nextTick } from "vue";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import Dialog from "primevue/dialog";
import Chart from "primevue/chart";
import html2canvas from "html2canvas";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage, rtdb } from "/firebaseConfig";
import { ref as dbRef, get } from "firebase/database";
import { useUserStore } from "@/store/user";
import LoadingScreen from "@/components/LoadingScreen.vue";
import StatsTabs from "@/components/StatsTabs.vue";
import {
  createBanner,
  updateBanner as updateBannerUtil,
  deleteBanner as deleteCustomBanner,
  toggleCustomBannerStatus,
  toggleLiveBannerStatus,
  changeBannerPriority as changeBannerPriorityUtil,
  uploadBannerImage,
  uploadBannerImageWithoutId,
  deleteBannerImage,
} from "@/utils/write/ad";

export default {
  name: "AdManagement",
  components: {
    LoadingScreen,
    StatsTabs,
    Dialog,
    Chart,
  },
  data() {
    return {
      loading: true,
      error: null,
      selectedAdType: "main", // 'main', 'detail', 또는 'live'
      liveBanners: [], // 라이브 배너 (bannerOn이거나 클릭 데이터가 있는 것)
      customBanners: [], // /banners 컬렉션의 배너
      detailAds: [],
      showBannerModal: false,
      editingBanner: null,
      saving: false,
      uploading: false,
      uploadProgress: 0,
      imagePreview: null,
      uploadedFileName: null,
      showReportModal: false,
      selectedAdForReport: null,
      showSimpleReportModal: false,
      selectedAdForSimpleReport: null,
      showInvoiceModal: false,
      selectedAdForInvoice: null,
      invoiceSaving: false,
      invoiceStartDate: "",
      invoiceEndDate: "",
      invoiceBusiness: {
        registrationNumber: "897-07-03002",
        companyName: "링꾹",
        representativeName: "이민경",
        address: "서울특별시 강서구 마곡중앙2로 15, 1층 103호",
        businessType: "정보통신업",
        businessItem: "포털 및 기타 인터넷 정보 매개 서비스업",
        bankName: "국민은행",
        accountHolder: "이민경 (링꾹)",
        accountNumber: "009901-04-203973",
      },
      visitorData: {}, // 방문자 통계 데이터
      detailPageVisitorData: {}, // detail 페이지 방문자 통계 데이터
      reportStartDate: null, // 보고서 시작 날짜
      reportEndDate: null, // 보고서 종료 날짜
      reportPeriodType: 'all', // 'all', 'week', 'month', '3months', 'custom'
      simpleReportBaseDate: null, // 간이 보고서 기준일 (null이면 오늘 또는 지난달 말일)
      bannerForm: {
        title: "",
        img: "",
        link: "",
        date: "",
        place: "",
        type: "main", // 기본값은 메인 배너
        active: true,
        startDateTime: "",
        endDateTime: "",
        priority: 0, // 우선순위 (숫자가 클수록 먼저 표시)
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "#17a2b8",
            borderWidth: 1,
            callbacks: {
              title: function (context) {
                return context[0].label;
              },
              label: function (context) {
                return `클릭: ${context.parsed.y}회`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              callback: function (value) {
                return value + "회";
              },
              color: "#666",
            },
          },
          x: {
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              color: "#666",
              maxRotation: 45,
              minRotation: 45,
            },
          },
        },
      },
      reportChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "#17a2b8",
            borderWidth: 1,
            callbacks: {
              title: function (context) {
                return context[0].label;
              },
              label: function (context) {
                return `클릭: ${context.parsed.y}회`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              callback: function (value) {
                return value + "회";
              },
              color: "#666",
            },
          },
          x: {
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              color: "#666",
              maxRotation: 45,
              minRotation: 45,
            },
          },
        },
      },
      reportViewChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "#28a745",
            borderWidth: 1,
            callbacks: {
              title: function (context) {
                return context[0].label;
              },
              label: function (context) {
                return `노출: ${context.parsed.y}회`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              callback: function (value) {
                return value + "회";
              },
              color: "#666",
            },
          },
          x: {
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              color: "#666",
              maxRotation: 45,
              minRotation: 45,
            },
          },
        },
      },
      simpleGrowthChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 25,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "#20c997",
            borderWidth: 1,
            callbacks: {
              label: function (context) {
                return `방문수: ${context.parsed.y.toLocaleString()}명`;
              },
            },
          },
        },
        animation: {
          onComplete: function (animation) {
            const chart = animation.chart;
            const ctx = chart.ctx;
            ctx.font = "bold 11px sans-serif";
            ctx.fillStyle = "#20c997";
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";

            chart.data.datasets.forEach(function (dataset, i) {
              const meta = chart.getDatasetMeta(i);
              meta.data.forEach(function (bar, index) {
                const data = dataset.data[index];
                ctx.fillText(data.toLocaleString(), bar.x, bar.y - 5);
              });
            });
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: "25%",
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              callback: function (value) {
                return value.toLocaleString() + "명";
              },
              color: "#666",
            },
          },
          x: {
            grid: {
              color: "rgba(0, 0, 0, 0.1)",
            },
            ticks: {
              color: "#666",
            },
          },
        },
      },
    };
  },
  computed: {
    userStore() {
      return useUserStore();
    },

    // 관리자 권한 확인
    isAdmin() {
      return this.userStore.auth === "admin";
    },

    // 모든 배너 - 현재 탭에 맞는 것만 필터링
    allBanners() {
      let filtered = [];

      if (this.selectedAdType === "main") {
        // 메인 배너 탭: type이 main인 커스텀 배너만
        filtered = this.customBanners.filter((banner) => banner.type === "main");
      } else if (this.selectedAdType === "detail") {
        // 상세 페이지 탭: type이 detail인 커스텀 배너만
        filtered = this.customBanners.filter((banner) => banner.type === "detail");
      } else if (this.selectedAdType === "live") {
        // 라이브 배너 탭: 모든 라이브 배너
        filtered = this.liveBanners;
      }

      // 정렬: 우선순위 -> 활성화 상태 -> 날짜순
      return filtered.sort((a, b) => {
        // 우선순위 (priority가 높을수록 먼저)
        const priorityA = a.priority || 0;
        const priorityB = b.priority || 0;
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        // 활성화된 배너가 먼저
        if (a.active !== b.active) {
          return b.active - a.active;
        }
        // 날짜순 정렬
        return new Date(b.date) - new Date(a.date);
      });
    },

    mainBannerCount() {
      return this.customBanners.filter((banner) => banner.type === "main").length;
    },

    detailAdCount() {
      return this.customBanners.filter((banner) => banner.type === "detail").length;
    },

    liveBannerCount() {
      return this.liveBanners.length;
    },

    isFormValid() {
      return (
        this.bannerForm.title && this.bannerForm.img && this.bannerForm.date && this.bannerForm.type
      );
    },
  },

  async created() {
    if (!this.isAdmin) {
      this.$router.push("/");
      return;
    }
    await this.fetchAds();
    await this.fetchVisitorData();
  },

  methods: {
    async fetchAds() {
      try {
        this.loading = true;
        this.error = null;

        // 1. 라이브 배너 (bannerOn이 true이거나 클릭 데이터가 있는 것들)
        const livesSnapshot = await getDocs(collection(firestore, "lives"));

        const liveBannerPromises = livesSnapshot.docs.map(async (liveDoc) => {
          const data = liveDoc.data();

          // bannerOn이 true이거나 bannerClicksByDate가 있는 것만 포함
          const hasClickData =
            data.bannerClicksByDate && Object.keys(data.bannerClicksByDate).length > 0;

          if (!data.bannerOn && !hasClickData) {
            return null;
          }

          let bannerUrl = null;
          if (data.banner) {
            try {
              if (data.banner.startsWith("http")) {
                bannerUrl = data.banner;
              } else {
                const imageRef = storageRef(storage, `banner/${data.banner}`);
                bannerUrl = await getDownloadURL(imageRef);
              }
            } catch (e) {
              console.warn(`배너 이미지 로드 실패 (${data.title}):`, e);
            }
          }

          return {
            id: liveDoc.id,
            source: "live", // 출처 표시
            title: data.title || "제목 없음",
            date: data.date || "",
            place: data.place || "",
            type: data.type || "vocal",
            active: data.bannerOn || false,
            banner: data.banner || null,
            bannerUrl: bannerUrl,
            clickCount: data.bannerClickCount || 0,
            clicksByDate: data.bannerClicksByDate || {},
            impressionCount: data.bannerImpressionCount || 0,
            impressionsByDate: data.bannerImpressionsByDate || {},
            priority: data.priority || 0, // 우선순위
            showStats: false,
            selectedMonth: null,
            updating: false,
            deleting: false,
          };
        });

        const liveBannersData = await Promise.all(liveBannerPromises);
        this.liveBanners = liveBannersData.filter(Boolean);

        // 2. 커스텀 배너 (/banners 컬렉션)
        const bannersSnapshot = await getDocs(collection(firestore, "banners"));

        const customBannerPromises = bannersSnapshot.docs.map(async (bannerDoc) => {
          const data = bannerDoc.data();

          let bannerUrl = null;
          if (data.img) {
            try {
              if (data.img.startsWith("http")) {
                bannerUrl = data.img;
              } else {
                const imageRef = storageRef(storage, `banner/${data.img}`);
                bannerUrl = await getDownloadURL(imageRef);
              }
            } catch (e) {
              console.warn(`배너 이미지 로드 실패 (${data.title}):`, e);
            }
          }

          return {
            id: bannerDoc.id,
            source: "custom", // 출처 표시
            title: data.title || "제목 없음",
            date: data.date || "",
            place: data.place || "",
            link: data.link || "",
            type: data.type || "main", // 타입 추가
            active: data.active !== undefined ? data.active : true,
            banner: data.img || null,
            bannerUrl: bannerUrl,
            clickCount: data.clickCount || 0,
            clicksByDate: data.clicksByDate || {},
            impressionCount: data.impressionCount || 0,
            impressionsByDate: data.impressionsByDate || {},
            startDateTime: data.startDateTime || null,
            endDateTime: data.endDateTime || null,
            priority: data.priority || 0, // 우선순위
            showStats: false,
            selectedMonth: null,
            updating: false,
            deleting: false,
          };
        });

        const customBannersData = await Promise.all(customBannerPromises);
        this.customBanners = customBannersData.filter(Boolean);

        // 상세 페이지 광고는 추후 구현
        this.detailAds = [];
      } catch (error) {
        console.error("광고 데이터 조회 오류:", error);
        this.error = "데이터를 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },

    async toggleBannerStatus(ad) {
      try {
        ad.updating = true;
        const newStatus =
          ad.source === "live"
            ? await toggleLiveBannerStatus(ad.id, ad.active)
            : await toggleCustomBannerStatus(ad.id, ad.active);
        ad.active = newStatus;
      } catch (error) {
        console.error("배너 상태 변경 오류:", error);
        alert("배너 상태 변경 중 오류가 발생했습니다.");
      } finally {
        ad.updating = false;
      }
    },

    openAddBannerModal() {
      this.editingBanner = null;
      this.bannerForm = {
        title: "",
        img: "",
        link: "",
        date: "",
        place: "",
        type: this.selectedAdType, // 현재 선택된 탭에 맞춰 기본값 설정
        active: true,
        startDateTime: "",
        endDateTime: "",
        priority: 0,
      };
      this.imagePreview = null;
      this.uploadedFileName = null;
      this.uploadProgress = 0;
      this.showBannerModal = true;
    },

    async editBanner(ad) {
      this.editingBanner = ad;
      this.bannerForm = {
        title: ad.title,
        img: ad.banner,
        link: ad.link || "",
        date: ad.date,
        place: ad.place || "",
        type: ad.type || "main",
        active: ad.active,
        startDateTime: ad.startDateTime || "",
        endDateTime: ad.endDateTime || "",
        priority: ad.priority || 0,
      };
      this.uploadedFileName = ad.banner;
      this.uploadProgress = 0;

      // 이미지 미리보기 로드
      if (ad.bannerUrl) {
        this.imagePreview = ad.bannerUrl;
      } else if (ad.banner) {
        try {
          if (ad.banner.startsWith("http")) {
            this.imagePreview = ad.banner;
          } else {
            const imageRef = storageRef(storage, `banner/${ad.banner}`);
            this.imagePreview = await getDownloadURL(imageRef);
          }
        } catch (e) {
          console.warn("이미지 미리보기 로드 실패:", e);
          this.imagePreview = null;
        }
      }

      this.showBannerModal = true;
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // 파일 크기 체크 (10MB 제한)
      if (file.size > 10 * 1024 * 1024) {
        alert("파일 크기는 10MB를 초과할 수 없습니다.");
        return;
      }

      // 이미지 파일 체크
      if (!file.type.startsWith("image/")) {
        alert("이미지 파일만 업로드할 수 있습니다.");
        return;
      }

      try {
        this.uploading = true;
        this.uploadProgress = 0;

        // 신규 추가 시에는 문서 생성 없이 이미지만 업로드, 수정 시에는 기존 문서에 연결
        const { downloadURL, fileName } = this.editingBanner
          ? await uploadBannerImage(
            file,
            this.editingBanner.id,
            (progress) => (this.uploadProgress = progress)
          )
          : await uploadBannerImageWithoutId(
            file,
            (progress) => (this.uploadProgress = progress)
          );

        this.bannerForm.img = fileName;
        this.uploadedFileName = fileName;
        this.imagePreview = downloadURL;
        this.uploading = false;

        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = "";
        }
      } catch (error) {
        console.error("이미지 업로드 오류:", error);
        alert("이미지 업로드 중 오류가 발생했습니다.");
        this.uploading = false;
      }
    },

    async handleManualInput() {
      // 수동으로 입력한 경우 미리보기 업데이트
      if (this.bannerForm.img) {
        try {
          if (this.bannerForm.img.startsWith("http")) {
            this.imagePreview = this.bannerForm.img;
          } else {
            const imageRef = storageRef(storage, `banner/${this.bannerForm.img}`);
            this.imagePreview = await getDownloadURL(imageRef);
          }
          this.uploadedFileName = this.bannerForm.img;
        } catch (e) {
          console.warn("이미지 미리보기 로드 실패:", e);
          this.imagePreview = null;
        }
      } else {
        this.imagePreview = null;
        this.uploadedFileName = null;
      }
    },

    async clearImage() {
      // 업로드한 파일이 있고, 새로 추가하는 경우(수정이 아닌 경우)에만 Storage에서 삭제
      if (this.uploadedFileName && !this.editingBanner) {
        try {
          await deleteBannerImage(this.uploadedFileName);
        } catch (error) {
          console.warn("이미지 삭제 오류:", error);
        }
      }

      this.bannerForm.img = "";
      this.imagePreview = null;
      this.uploadedFileName = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    async saveBanner() {
      if (!this.isFormValid) {
        alert("필수 항목을 모두 입력해주세요.");
        return;
      }

      try {
        this.saving = true;
        const bannerData = {
          title: this.bannerForm.title,
          img: this.bannerForm.img,
          link: this.bannerForm.link || "",
          date: this.bannerForm.date,
          place: this.bannerForm.place || "",
          type: this.bannerForm.type, // 타입 추가
          active: this.bannerForm.active,
          priority: parseInt(this.bannerForm.priority) || 0, // 우선순위
        };

        // 상세 페이지 광고인 경우 광고 기간 추가
        if (this.bannerForm.type === "detail") {
          bannerData.startDateTime = this.bannerForm.startDateTime || null;
          bannerData.endDateTime = this.bannerForm.endDateTime || null;
        }

        if (this.editingBanner) {
          // 수정
          await updateBannerUtil(this.editingBanner.id, bannerData);
          alert("배너가 수정되었습니다.");
        } else {
          // 추가
          await createBanner(bannerData);
          alert("배너가 추가되었습니다.");
        }

        this.showBannerModal = false;
        await this.fetchAds(); // 목록 새로고침
      } catch (error) {
        console.error("배너 저장 오류:", error);
        alert("배너 저장 중 오류가 발생했습니다.");
      } finally {
        this.saving = false;
      }
    },

    async deleteBanner(ad) {
      if (!confirm(`"${ad.title}" 배너를 삭제하시겠습니까?`)) {
        return;
      }

      try {
        ad.deleting = true;
        await deleteCustomBanner(ad.id);

        // 목록에서 제거
        const index = this.customBanners.findIndex((b) => b.id === ad.id);
        if (index !== -1) {
          this.customBanners.splice(index, 1);
        }

        alert("배너가 삭제되었습니다.");
      } catch (error) {
        console.error("배너 삭제 오류:", error);
        alert("배너 삭제 중 오류가 발생했습니다.");
      } finally {
        ad.deleting = false;
      }
    },

    goToLiveDetail(liveId) {
      this.$router.push({
        name: "LiveDetail",
        params: { slug: liveId },
      });
    },

    getTypeLabel(type) {
      const typeLabels = {
        vocal: "보컬",
        band: "밴드",
        mix: "믹스",
      };
      return typeLabels[type] || type;
    },

    hasClickData(ad) {
      const hasClicks = ad.clicksByDate && Object.keys(ad.clicksByDate).length > 0;
      const hasImpressions = ad.impressionsByDate && Object.keys(ad.impressionsByDate).length > 0;
      return hasClicks || hasImpressions;
    },

    toggleClickStats(adId) {
      // 라이브 배너 또는 커스텀 배너에서 찾기
      const ad =
        this.liveBanners.find((b) => b.id === adId) ||
        this.customBanners.find((b) => b.id === adId);
      if (ad) {
        ad.showStats = !ad.showStats;
      }
    },

    getAvailableMonths(ad) {
      if (!ad.clicksByDate && !ad.impressionsByDate) return ["all"];

      // 클릭과 노출 데이터에서 모든 날짜의 월 추출 (YYYY-MM 형식)
      const months = new Set();

      // 클릭 데이터에서 월 추출
      if (ad.clicksByDate) {
        Object.keys(ad.clicksByDate).forEach((date) => {
          const month = date.substring(0, 7); // YYYY-MM
          months.add(month);
        });
      }

      // 노출 데이터에서 월 추출
      if (ad.impressionsByDate) {
        Object.keys(ad.impressionsByDate).forEach((date) => {
          const month = date.substring(0, 7); // YYYY-MM
          months.add(month);
        });
      }

      // 월을 정렬하고 "전체" 옵션 추가
      const sortedMonths = Array.from(months).sort().reverse();
      return ["all", ...sortedMonths];
    },

    selectMonth(adId, month) {
      const ad =
        this.liveBanners.find((b) => b.id === adId) ||
        this.customBanners.find((b) => b.id === adId);
      if (ad) {
        ad.selectedMonth = month === "all" ? null : month;
      }
    },

    formatMonthLabel(month) {
      if (month === "all") return "전체";
      const [year, monthNum] = month.split("-");
      return `${year}년 ${parseInt(monthNum)}월`;
    },

    getChartData(ad) {
      const filteredClicks = this.showReportModal ? this.getFilteredClicksByDate(ad) : ad.clicksByDate;

      if (!filteredClicks && !ad.impressionsByDate) return { labels: [], datasets: [] };

      // 클릭과 노출 데이터에서 모든 날짜 수집
      const allDates = new Set([
        ...Object.keys(filteredClicks || {}),
        ...Object.keys(ad.impressionsByDate || {}),
      ]);

      // 날짜별로 정렬 (과거 -> 최신)
      let sortedDates = Array.from(allDates).sort((a, b) => a.localeCompare(b));

      // 보고서 모달이 아닐 때만 월 필터 적용
      if (!this.showReportModal && ad.selectedMonth) {
        sortedDates = sortedDates.filter((date) => date.startsWith(ad.selectedMonth));
      }

      // 보고서 모달일 때 기간 필터 적용
      if (this.showReportModal) {
        sortedDates = sortedDates.filter((date) => this.isDateInReportPeriod(date));
      }

      const labels = sortedDates.map((date) => {
        const d = new Date(date + "T00:00:00");
        const month = d.getMonth() + 1;
        const day = d.getDate();
        const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
        const weekday = weekdays[d.getDay()];
        return `${month}/${day}(${weekday})`;
      });

      const impressionData = sortedDates.map((date) => (ad.impressionsByDate && ad.impressionsByDate[date]) || 0);
      const clickData = sortedDates.map((date) => (filteredClicks && filteredClicks[date]) || 0);

      const datasets = [];

      // 노출수 데이터셋
      if (ad.impressionsByDate && Object.keys(ad.impressionsByDate).length > 0) {
        datasets.push({
          label: "노출 수",
          data: impressionData,
          backgroundColor: "rgba(102, 189, 255, 0.6)",
          borderColor: "#66bdff",
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
        });
      }

      // 클릭수 데이터셋
      if (filteredClicks && Object.keys(filteredClicks).length > 0) {
        datasets.push({
          label: "클릭 수",
          data: clickData,
          backgroundColor: "rgba(23, 162, 184, 0.8)",
          borderColor: "#17a2b8",
          borderWidth: 2,
          borderRadius: 6,
          borderSkipped: false,
        });
      }

      return {
        labels,
        datasets,
      };
    },

    // 날짜/시간 포맷팅
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return "";

      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      return `${year}.${month}.${day} ${hours}:${minutes}`;
    },

    // 광고 기간이 활성화 상태인지 확인
    isAdPeriodActive(ad) {
      if (!ad.startDateTime && !ad.endDateTime) {
        return true; // 기간 설정이 없으면 항상 활성
      }

      const now = new Date();
      const start = ad.startDateTime ? new Date(ad.startDateTime) : null;
      const end = ad.endDateTime ? new Date(ad.endDateTime) : null;

      if (start && now < start) {
        return false; // 시작 전
      }

      if (end && now > end) {
        return false; // 종료됨
      }

      return true; // 기간 내
    },

    // 광고 기간 상태 텍스트
    getAdPeriodStatus(ad) {
      if (!ad.startDateTime && !ad.endDateTime) {
        return "상시 노출";
      }

      const now = new Date();
      const start = ad.startDateTime ? new Date(ad.startDateTime) : null;
      const end = ad.endDateTime ? new Date(ad.endDateTime) : null;

      if (start && now < start) {
        return "노출 전";
      }

      if (end && now > end) {
        return "노출 종료";
      }

      return "노출 중";
    },

    /**
     * 광고 우선순위 변경
     */
    async changePriority(ad, change) {
      try {
        ad.updating = true;

        const newPriority = Math.max(0, (ad.priority || 0) + change);
        const updated = await changeBannerPriorityUtil(ad.source, ad.id, newPriority);
        ad.priority = updated;
      } catch (error) {
        console.error("우선순위 변경 오류:", error);
        alert("우선순위 변경 중 오류가 발생했습니다.");
      } finally {
        ad.updating = false;
      }
    },

    /**
     * 방문자 데이터 가져오기
     */
    async fetchVisitorData() {
      try {
        // 전체 방문자 데이터 (기존)
        const visitorRef = dbRef(rtdb, "visitorCount");
        const snapshot = await get(visitorRef);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const processedData = {};
          Object.keys(data).forEach((date) => {
            const count = data[date];
            if (typeof count === "number") {
              processedData[date] = count;
            } else if (typeof count === "object" && count?.all !== undefined) {
              processedData[date] = count.all;
            } else {
              processedData[date] = 0;
            }
          });
          this.visitorData = processedData;
        } else {
          this.visitorData = {};
        }

        // detail 페이지 방문자 데이터 가져오기
        await this.fetchDetailPageVisitorData();
      } catch (error) {
        console.error("방문자 데이터 가져오기 실패:", error);
        this.visitorData = {};
        this.detailPageVisitorData = {};
      }
    },

    /**
     * detail 페이지 방문자 데이터 가져오기 (trafficSources에서)
     */
    async fetchDetailPageVisitorData() {
      try {
        // 최근 90일 데이터 가져오기
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 90);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date();
        endDate.setHours(23, 59, 59, 999);

        const trafficRef = collection(firestore, "trafficSources");
        const q = query(
          trafficRef,
          where("timestamp", ">=", startDate),
          where("timestamp", "<=", endDate),
          orderBy("timestamp", "desc")
        );

        const snapshot = await getDocs(q);
        const detailPageData = {};

        snapshot.docs.forEach((doc) => {
          const data = doc.data();
          const landingPage = data.landingPage || "";

          // detail 페이지(/live/로 시작하는 경로)만 필터링
          if (landingPage.startsWith("/live/")) {
            const date = data.timestamp?.toDate?.()?.toISOString()?.split("T")[0];
            if (date) {
              detailPageData[date] = (detailPageData[date] || 0) + 1;
            }
          }
        });

        this.detailPageVisitorData = detailPageData;
      } catch (error) {
        console.error("detail 페이지 방문자 데이터 가져오기 실패:", error);
        this.detailPageVisitorData = {};
      }
    },

    /**
     * 통계 보고서 모달 열기
     */
    openReportModal(ad) {
      this.selectedAdForReport = ad;
      this.reportPeriodType = 'all';
      this.reportStartDate = null;
      this.reportEndDate = null;
      this.showReportModal = true;
    },

    /**
     * 보고서 기간 설정
     */
    setReportPeriod(type) {
      this.reportPeriodType = type;
      const now = new Date();

      if (type === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(now.getDate() - 7);
        this.reportStartDate = this.formatDateForInput(weekAgo);
        this.reportEndDate = this.formatDateForInput(now);
      } else if (type === 'month') {
        const monthAgo = new Date(now);
        monthAgo.setMonth(now.getMonth() - 1);
        this.reportStartDate = this.formatDateForInput(monthAgo);
        this.reportEndDate = this.formatDateForInput(now);
      } else if (type === '3months') {
        const threeMonthsAgo = new Date(now);
        threeMonthsAgo.setMonth(now.getMonth() - 3);
        this.reportStartDate = this.formatDateForInput(threeMonthsAgo);
        this.reportEndDate = this.formatDateForInput(now);
      } else if (type === 'all') {
        this.reportStartDate = null;
        this.reportEndDate = null;
      }
    },

    /**
     * 날짜를 input date 형식으로 변환 (YYYY-MM-DD)
     */
    formatDateForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    /**
     * 보고서 기간 텍스트 반환
     */
    getReportPeriodText() {
      if (this.reportPeriodType === 'all') {
        return '전체 기간';
      }
      if (this.reportStartDate && this.reportEndDate) {
        return `${this.reportStartDate} ~ ${this.reportEndDate}`;
      }
      return '';
    },

    /**
     * 날짜가 보고서 기간 내에 있는지 확인
     */
    isDateInReportPeriod(dateString) {
      if (this.reportPeriodType === 'all') {
        return true;
      }
      if (!this.reportStartDate || !this.reportEndDate) {
        return true;
      }
      return dateString >= this.reportStartDate && dateString <= this.reportEndDate;
    },

    /**
     * 기간이 필터링된 클릭 데이터 반환
     */
    getFilteredClicksByDate(ad) {
      if (!ad.clicksByDate) {
        return {};
      }
      if (this.reportPeriodType === 'all') {
        return ad.clicksByDate;
      }

      const filtered = {};
      for (const [date, count] of Object.entries(ad.clicksByDate)) {
        if (this.isDateInReportPeriod(date)) {
          filtered[date] = count;
        }
      }
      return filtered;
    },

    /**
     * 통계 기간 계산 (필터링 적용)
     */
    getStatsPeriod(ad) {
      const filteredData = this.getFilteredClicksByDate(ad);
      if (!filteredData || Object.keys(filteredData).length === 0) {
        return "-";
      }
      const dates = Object.keys(filteredData).sort();
      const startDate = dates[0];
      const endDate = dates[dates.length - 1];
      return `${startDate} ~ ${endDate}`;
    },

    /**
     * 평균 일일 클릭 수 계산 (필터링 적용)
     */
    getAverageDailyClicks(ad) {
      const filteredData = this.getFilteredClicksByDate(ad);
      if (!filteredData || Object.keys(filteredData).length === 0) {
        return 0;
      }
      const totalClicks = Object.values(filteredData).reduce((sum, count) => sum + count, 0);
      const days = Object.keys(filteredData).length;
      return Math.round((totalClicks / days) * 10) / 10; // 소수점 첫째자리까지
    },

    /**
     * 최대 일일 클릭 수 계산 (필터링 적용)
     */
    getMaxDailyClicks(ad) {
      const filteredData = this.getFilteredClicksByDate(ad);
      if (!filteredData || Object.keys(filteredData).length === 0) {
        return 0;
      }
      return Math.max(...Object.values(filteredData));
    },

    /**
     * 정렬된 통계 데이터 반환 (최신순) - 노출/클릭 통합 (필터링 적용)
     */
    getSortedClickData(ad) {
      const filteredClicks = this.getFilteredClicksByDate(ad);

      // 클릭과 노출 데이터에서 모든 날짜 수집
      const allDates = new Set([
        ...Object.keys(filteredClicks || {}),
        ...Object.keys(ad.impressionsByDate || {}),
      ]);

      // 날짜별 데이터를 [날짜, 노출수, 클릭수] 형태로 변환하고 정렬
      return Array.from(allDates)
        .filter((date) => this.isDateInReportPeriod(date))
        .map((date) => {
          const impressions = (ad.impressionsByDate && ad.impressionsByDate[date]) || 0;
          const clicks = (filteredClicks && filteredClicks[date]) || 0;
          return [date, impressions, clicks];
        })
        .sort((a, b) => b[0].localeCompare(a[0])); // 최신순 정렬
    },

    /**
     * 보고서용 날짜 포맷팅
     */
    formatDateForReport(dateString) {
      const date = new Date(dateString + "T00:00:00");
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
      const weekday = weekdays[date.getDay()];
      return `${year}.${month}.${day} (${weekday})`;
    },

    /**
     * 노출 데이터가 있는지 확인 (detail 페이지 방문자 통계 기반)
     */
    hasViewData() {
      // detail 페이지 방문자 통계 데이터가 있으면 노출 데이터가 있다고 간주
      return Object.keys(this.detailPageVisitorData).length > 0;
    },

    /**
     * 노출 통계 기간 계산 (detail 페이지 방문자 통계 기반, 필터링 적용)
     */
    getViewStatsPeriod() {
      const filtered = {};
      for (const [date, count] of Object.entries(this.detailPageVisitorData)) {
        if (this.isDateInReportPeriod(date)) {
          filtered[date] = count;
        }
      }

      if (Object.keys(filtered).length === 0) {
        return "-";
      }
      const dates = Object.keys(filtered).sort();
      const startDate = dates[0];
      const endDate = dates[dates.length - 1];
      return `${startDate} ~ ${endDate}`;
    },

    /**
     * 평균 일일 노출 수 계산 (detail 페이지 방문자 통계 기반, 필터링 적용)
     */
    getAverageDailyViews() {
      const filtered = {};
      for (const [date, count] of Object.entries(this.detailPageVisitorData)) {
        if (this.isDateInReportPeriod(date)) {
          filtered[date] = count;
        }
      }

      if (Object.keys(filtered).length === 0) {
        return 0;
      }
      const totalViews = Object.values(filtered).reduce((sum, count) => sum + count, 0);
      const days = Object.keys(filtered).length;
      return Math.round((totalViews / days) * 10) / 10; // 소수점 첫째자리까지
    },

    /**
     * 최대 일일 노출 수 계산 (detail 페이지 방문자 통계 기반, 필터링 적용)
     */
    getMaxDailyViews() {
      const filtered = {};
      for (const [date, count] of Object.entries(this.detailPageVisitorData)) {
        if (this.isDateInReportPeriod(date)) {
          filtered[date] = count;
        }
      }

      if (Object.keys(filtered).length === 0) {
        return 0;
      }
      return Math.max(...Object.values(filtered));
    },

    /**
     * 정렬된 노출 데이터 반환 (최신순, detail 페이지 방문자 통계 기반)
     */
    getSortedViewData() {
      if (Object.keys(this.detailPageVisitorData).length === 0) return [];
      return Object.entries(this.detailPageVisitorData)
        .sort((a, b) => b[0].localeCompare(a[0])); // 최신순 정렬
    },

    /**
     * 노출 차트 데이터 생성 (detail 페이지 방문자 통계 기반, 필터링 적용)
     */
    getViewChartData() {
      if (Object.keys(this.detailPageVisitorData).length === 0) return { labels: [], datasets: [] };

      // 기간 필터링 적용
      const filtered = {};
      for (const [date, count] of Object.entries(this.detailPageVisitorData)) {
        if (this.isDateInReportPeriod(date)) {
          filtered[date] = count;
        }
      }

      // 날짜별로 정렬 (과거 -> 최신)
      const sortedEntries = Object.entries(filtered).sort((a, b) => a[0].localeCompare(b[0]));

      const labels = sortedEntries.map(([date]) => {
        const d = new Date(date + "T00:00:00");
        const month = d.getMonth() + 1;
        const day = d.getDate();
        const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
        const weekday = weekdays[d.getDay()];
        return `${month}/${day}(${weekday})`;
      });

      const data = sortedEntries.map(([, count]) => count);

      return {
        labels,
        datasets: [
          {
            label: "노출 수",
            data,
            backgroundColor: "rgba(40, 167, 69, 0.8)",
            borderColor: "#28a745",
            borderWidth: 2,
            borderRadius: 6,
            borderSkipped: false,
          },
        ],
      };
    },

    /**
     * 특정 날짜의 노출 수 반환 (detail 페이지 방문자 통계 기반)
     */
    getViewCountForDate(ad, date) {
      return this.detailPageVisitorData[date] || 0;
    },

    /**
     * 클릭률 (CTR) 계산 (실제 광고 노출수 기반)
     */
    getCTR(ad) {
      const impressions = ad.impressionCount || 0;
      if (impressions === 0) return "0.00";
      const ctr = ((ad.clickCount || 0) / impressions) * 100;
      return ctr.toFixed(2);
    },

    /**
     * 일일 클릭률 계산
     */
    getDailyCTR(ad, date) {
      const impressionCount = ad.impressionsByDate && ad.impressionsByDate[date] ? ad.impressionsByDate[date] : 0;
      if (!impressionCount || impressionCount === 0) return "0.00";
      const clickCount = ad.clicksByDate && ad.clicksByDate[date] ? ad.clicksByDate[date] : 0;
      const ctr = (clickCount / impressionCount) * 100;
      return ctr.toFixed(2);
    },

    /**
     * 총 노출 수 계산 (실제 광고 노출수, 필터링 적용)
     */
    getTotalViews() {
      if (!this.selectedAdForReport) return 0;

      // 전체 기간이면 impressionCount 사용
      if (this.reportPeriodType === 'all') {
        return this.selectedAdForReport.impressionCount || 0;
      }

      // 기간 필터링이 있으면 impressionsByDate에서 계산
      const impressionsByDate = this.selectedAdForReport.impressionsByDate || {};
      return Object.entries(impressionsByDate)
        .filter(([date]) => this.isDateInReportPeriod(date))
        .reduce((sum, [, count]) => sum + count, 0);
    },

    /**
     * 총 클릭 수 계산 (필터링 적용)
     */
    getTotalClicks() {
      if (!this.selectedAdForReport) return 0;

      // 전체 기간이면 clickCount 사용
      if (this.reportPeriodType === 'all') {
        return this.selectedAdForReport.clickCount || 0;
      }

      // 기간 필터링이 있으면 필터링된 clicksByDate에서 계산
      const filteredClicks = this.getFilteredClicksByDate(this.selectedAdForReport);
      return Object.values(filteredClicks).reduce((sum, count) => sum + count, 0);
    },

    /**
     * 필터링된 CTR 계산
     */
    getFilteredCTR() {
      const impressions = this.getTotalViews();
      if (impressions === 0) return "0.00";
      const clicks = this.getTotalClicks();
      const ctr = (clicks / impressions) * 100;
      return ctr.toFixed(2);
    },

    /**
     * 현재 날짜 포맷팅 (보고서용)
     */
    getCurrentDate() {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      return `${year}.${month}.${day}`;
    },

    /**
     * 로컬 기준 YYYY-MM-DD 포맷
     */
    formatDateYYYYMMDD(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },

    /**
     * 로컬 기준 YYYY.MM.DD 포맷
     */
    formatDateYYYYMMDot(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}.${month}.${day}`;
    },

    /**
     * 기준일 반환 (간이 보고서용)
     * simpleReportBaseDate가 설정되어 있으면 해당 날짜, 아니면 오늘
     */
    getSimpleReportBaseDate() {
      if (this.simpleReportBaseDate) {
        const date = new Date(this.simpleReportBaseDate);
        date.setHours(12, 0, 0, 0);
        return date;
      }

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      today.setHours(12, 0, 0, 0);

      return today;
    },

    /**
     * 지난달 말일(Date) 반환 (로컬 기준)
     * 기준일을 기반으로 계산
     */
    getLastMonthEndDate() {
      const baseDate = this.getSimpleReportBaseDate();
      // day=0 => 전달의 마지막 날
      const end = new Date(baseDate.getFullYear(), baseDate.getMonth(), 0);
      end.setHours(12, 0, 0, 0);
      return end;
    },

    /**
     * 간이성과보고서 발행일
     */
    getSimpleReportIssueDate() {
      const baseDate = this.getSimpleReportBaseDate();
      return this.formatDateYYYYMMDot(baseDate);
    },

    /**
     * 간이 보고서 모달 열기
     */
    openSimpleReportModal(ad) {
      this.selectedAdForSimpleReport = ad;
      this.simpleReportBaseDate = null; // 초기화
      this.showSimpleReportModal = true;
    },

    /**
     * Invoice 미리보기 모달 열기
     */
    openInvoiceModal(ad) {
      this.selectedAdForInvoice = ad;
      this.invoiceStartDate = this.getDefaultInvoiceStartDate(ad);
      this.invoiceEndDate = this.getDefaultInvoiceEndDate(ad);
      this.showInvoiceModal = true;
    },

    /**
     * Invoice 문서 번호 생성
     */
    getInvoiceNumber(ad) {
      if (!ad?.id) {
        return `INV-${this.getCurrentDate().replace(/\./g, "")}`;
      }
      return `INV-${this.getCurrentDate().replace(/\./g, "")}-${String(ad.id)
        .substring(0, 6)
        .toUpperCase()}`;
    },

    /**
     * Invoice용 광고 유형명
     */
    getInvoiceAdType(ad) {
      if (!ad) return "-";
      if (ad.source === "live") {
        return "라이브 연동 광고";
      }
      return ad.type === "detail" ? "상세 페이지 광고" : "메인 배너";
    },

    /**
     * Invoice용 광고 기간 텍스트
     */
    getInvoiceExposurePeriod(ad) {
      const startText = this.invoiceStartDate ? this.formatInvoicePeriodDate(this.invoiceStartDate) : "";
      const endText = this.invoiceEndDate ? this.formatInvoicePeriodDate(this.invoiceEndDate) : "";

      if (startText || endText) {
        const start = startText || "즉시";
        const end = endText || "상시";
        return `${start} ~ ${end}`;
      }

      if (!ad) return "-";
      if (ad.startDateTime || ad.endDateTime) {
        const start = ad.startDateTime ? this.formatDateTime(ad.startDateTime) : "즉시";
        const end = ad.endDateTime ? this.formatDateTime(ad.endDateTime) : "상시";
        return `${start} ~ ${end}`;
      }
      if (ad.date) {
        const normalizedDate = this.normalizeInvoiceDateValue(ad.date);
        if (normalizedDate) {
          return `${this.formatInvoicePeriodDate(normalizedDate)} ~ ${this.formatInvoicePeriodDate(normalizedDate)}`;
        }
      }
      return ad.date || "-";
    },

    /**
     * Invoice 기본 시작일 계산
     */
    getDefaultInvoiceStartDate(ad) {
      return this.normalizeInvoiceDateValue(ad?.startDateTime || ad?.date || "");
    },

    /**
     * Invoice 기본 종료일 계산
     */
    getDefaultInvoiceEndDate(ad) {
      return this.normalizeInvoiceDateValue(ad?.endDateTime || ad?.startDateTime || ad?.date || "");
    },

    /**
     * Invoice 기간 입력값 표준화
     */
    normalizeInvoiceDateValue(value) {
      if (!value) return "";
      if (typeof value !== "string") return "";

      const trimmed = value.trim();
      if (!trimmed) return "";

      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }

      if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
        return trimmed.slice(0, 10);
      }

      if (/^\d{4}\.\d{2}\.\d{2}$/.test(trimmed)) {
        return trimmed.replace(/\./g, "-");
      }

      const parsed = new Date(trimmed);
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatDateForInput(parsed);
      }

      return "";
    },

    /**
     * Invoice 기간 표시 포맷
     */
    formatInvoicePeriodDate(dateString) {
      if (!dateString) return "-";
      return dateString.replace(/-/g, ".");
    },

    /**
     * Invoice용 링크 표시 텍스트
     */
    getInvoiceLinkDisplay(ad) {
      return ad?.link || "링크 없음";
    },

    /**
     * Invoice 품목명
     */
    getInvoiceItemName(ad) {
      if (!ad) return "광고 집행";
      return ad.type === "detail" ? "상세페이지 광고" : ad.source === "live" ? "라이브 배너 광고" : "메인 배너 광고";
    },

    /**
     * Invoice 품목 상세 설명
     */
    getInvoiceLineDescription(ad) {
      if (!ad) return "-";
      const period = this.getInvoiceExposurePeriod(ad);
      return `${this.getInvoiceAdType(ad)} / ${period}`;
    },

    /**
     * 공급가액
     */
    getInvoiceSupplyAmount() {
      return 20000;
    },

    /**
     * VAT 금액
     */
    getInvoiceVatAmount() {
      return Math.round(this.getInvoiceSupplyAmount() * 0.1);
    },

    /**
     * 총 합계 금액
     */
    getInvoiceTotalAmount() {
      return this.getInvoiceSupplyAmount() + this.getInvoiceVatAmount();
    },

    /**
     * 원화 금액 포맷
     */
    formatInvoiceCurrency(value) {
      return `${Number(value || 0).toLocaleString()}원`;
    },

    /**
     * 파일명에 쓸 수 없는 문자 제거
     */
    sanitizeFileName(value) {
      const sanitized = String(value || "invoice")
        .replace(/[\\/:*?"<>|]/g, "")
        .trim()
        .replace(/\s+/g, "-");
      return sanitized || "invoice";
    },

    /**
     * 캡처 전 이미지 로딩 대기
     */
    async waitForImages(container) {
      const images = Array.from(container.querySelectorAll("img"));
      await Promise.all(
        images.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) {
                resolve();
                return;
              }
              img.onload = () => resolve();
              img.onerror = () => resolve();
            })
        )
      );
    },

    /**
     * Invoice 미리보기를 PNG로 저장
     */
    async saveInvoiceImage() {
      if (this.invoiceSaving || !this.$refs.invoicePreview) {
        return;
      }

      try {
        this.invoiceSaving = true;
        await nextTick();
        await this.waitForImages(this.$refs.invoicePreview);

        const canvas = await html2canvas(this.$refs.invoicePreview, {
          backgroundColor: "#ffffff",
          scale: 2,
          useCORS: true,
          logging: false,
        });

        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = `${this.sanitizeFileName(this.selectedAdForInvoice?.title)}-invoice.png`;
        link.click();

        if (this.$alert) {
          this.$alert("Invoice 이미지가 저장되었습니다.");
        }
      } catch (error) {
        console.error("Invoice 이미지 저장 실패:", error);
        if (this.$alert) {
          this.$alert("Invoice 이미지 저장에 실패했습니다. 다시 시도해 주세요.");
        }
      } finally {
        this.invoiceSaving = false;
      }
    },

    /**
     * 간이 보고서 기준월 변경
     */
    changeSimpleReportMonth(direction) {
      const currentBase = this.getSimpleReportBaseDate();
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);

      if (direction === -1) {
        // 이전달: 이전달의 말일로 설정
        const prevMonth = new Date(currentBase.getFullYear(), currentBase.getMonth(), 0);
        prevMonth.setHours(12, 0, 0, 0);
        this.simpleReportBaseDate = this.formatDateYYYYMMDD(prevMonth);
      } else if (direction === 1) {
        // 다음달: 다음달의 같은 날 또는 오늘 중 작은 값
        const nextMonth = new Date(currentBase);
        nextMonth.setMonth(nextMonth.getMonth() + 1);

        // 오늘을 넘지 않도록 제한
        if (nextMonth > today) {
          // 오늘이 더 작으면 오늘로 설정
          this.simpleReportBaseDate = this.formatDateYYYYMMDD(today);
        } else {
          this.simpleReportBaseDate = this.formatDateYYYYMMDD(nextMonth);
        }
      }
    },

    /**
     * 다음달로 갈 수 있는지 확인
     */
    canGoNextMonth() {
      const currentBase = this.getSimpleReportBaseDate();
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);

      // 현재 기준일이 오늘보다 이전이면 다음달로 갈 수 있음
      return currentBase < today;
    },

    /**
     * 최근 3개월 날짜 범위 계산
     */
    get3MonthDateRange() {
      // 기준일을 기반으로 계산
      const baseDate = this.getSimpleReportBaseDate();
      const endDate = new Date(baseDate);
      endDate.setHours(12, 0, 0, 0);

      // 최근 3개월 = 기준일로부터 3개월 전 1일 ~ 기준일
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 2, 1);
      startDate.setHours(12, 0, 0, 0);

      return {
        start: this.formatDateYYYYMMDD(startDate),
        end: this.formatDateYYYYMMDD(endDate),
      };
    },

    /**
     * 간이 보고서 기간 텍스트 (헤더용)
     */
    getSimple3MonthPeriodText() {
      const { start, end } = this.get3MonthDateRange();
      const startFormatted = start.replace(/-/g, '.');
      const endFormatted = end.replace(/-/g, '.');
      return `${startFormatted} ~ ${endFormatted} (최근 3개월)`;
    },

    /**
     * 최근 3개월 총 클릭수
     */
    getSimple3MonthClicks() {
      if (!this.selectedAdForSimpleReport?.clicksByDate) return 0;

      const { start, end } = this.get3MonthDateRange();
      let total = 0;

      Object.entries(this.selectedAdForSimpleReport.clicksByDate).forEach(([date, count]) => {
        if (date >= start && date <= end) {
          total += count;
        }
      });

      return total;
    },

    /**
     * 최근 3개월 클릭 데이터가 있는 일수
     */
    getSimple3MonthClickDays() {
      if (!this.selectedAdForSimpleReport?.clicksByDate) return 0;

      const { start, end } = this.get3MonthDateRange();
      let days = 0;

      Object.keys(this.selectedAdForSimpleReport.clicksByDate).forEach((date) => {
        if (date >= start && date <= end) {
          days++;
        }
      });

      return days;
    },

    /**
     * 최근 3개월 총 방문자 수 (visitorData 기반)
     */
    getSimple3MonthTotalVisitors() {
      if (!this.visitorData || Object.keys(this.visitorData).length === 0) return 0;

      const { start, end } = this.get3MonthDateRange();
      let total = 0;

      Object.entries(this.visitorData).forEach(([date, count]) => {
        if (date >= start && date <= end) {
          total += count;
        }
      });

      return total;
    },

    /**
     * 최근 3개월 일평균 방문자 수
     */
    getSimple3MonthAvgVisitors() {
      if (!this.visitorData || Object.keys(this.visitorData).length === 0) return 0;

      const { start, end } = this.get3MonthDateRange();
      let total = 0;
      let days = 0;

      Object.entries(this.visitorData).forEach(([date, count]) => {
        if (date >= start && date <= end) {
          total += count;
          days++;
        }
      });

      if (days === 0) return 0;
      return Math.round(total / days);
    },

    /**
     * 최근 3개월 총 노출 수 (impressionsByDate 기반)
     */
    getSimple3MonthTotalImpressions() {
      if (!this.selectedAdForSimpleReport?.impressionsByDate) return 0;

      const { start, end } = this.get3MonthDateRange();
      let total = 0;

      Object.entries(this.selectedAdForSimpleReport.impressionsByDate).forEach(([date, count]) => {
        if (date >= start && date <= end) {
          total += count;
        }
      });

      return total;
    },

    /**
     * 간이 보고서용 추정 클릭률 (노출수 기준)
     */
    getSimpleCTR() {
      const clicks = this.getSimple3MonthClicks();
      const impressions = this.getSimple3MonthTotalImpressions();

      if (impressions === 0) return "0.00";
      const ctr = (clicks / impressions) * 100;
      return ctr.toFixed(2);
    },

    /**
     * 6개월 성장추이 차트 데이터 (PrimeVue Chart용)
     */
    get6MonthChartData() {
      const growthData = this.get6MonthGrowthData();

      return {
        labels: growthData.map((d) => d.label),
        datasets: [
          {
            label: "방문수",
            data: growthData.map((d) => d.count),
            backgroundColor: "rgba(32, 201, 151, 0.8)",
            borderColor: "#20c997",
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          },
        ],
      };
    },

    /**
     * 6개월 월별 방문자 데이터 가져오기
     */
    get6MonthGrowthData() {
      const result = [];
      // 기준일을 사용 (기준일이 속한 달까지 포함)
      const baseDate = this.getSimpleReportBaseDate();

      for (let i = 5; i >= 0; i--) {
        const targetDate = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
        const year = targetDate.getFullYear();
        const month = targetDate.getMonth();

        // 해당 월의 시작일과 종료일
        const monthStartDate = new Date(year, month, 1);
        let monthEndDate;

        // 마지막 달(기준일이 속한 달)인 경우 기준일까지만
        if (i === 0) {
          monthEndDate = new Date(baseDate);
        } else {
          monthEndDate = new Date(year, month + 1, 0);
        }

        monthStartDate.setHours(12, 0, 0, 0);
        monthEndDate.setHours(12, 0, 0, 0);

        const startStr = this.formatDateYYYYMMDD(monthStartDate);
        const endStr = this.formatDateYYYYMMDD(monthEndDate);

        let monthTotal = 0;

        if (this.visitorData && Object.keys(this.visitorData).length > 0) {
          Object.entries(this.visitorData).forEach(([date, count]) => {
            if (date >= startStr && date <= endStr) {
              monthTotal += count;
            }
          });
        }

        result.push({
          label: `${month + 1}월`,
          count: monthTotal,
          percentage: 0, // 나중에 계산
        });
      }

      // 최대값 기준 퍼센티지 계산
      const maxCount = Math.max(...result.map((d) => d.count), 1);
      result.forEach((d) => {
        d.percentage = Math.round((d.count / maxCount) * 100);
      });

      return result;
    },

    /**
     * 6개월 총 방문자 수
     */
    get6MonthTotalVisitors() {
      const data = this.get6MonthGrowthData();
      return data.reduce((sum, d) => sum + d.count, 0);
    },

    /**
     * 6개월 월평균 방문자 수
     */
    get6MonthAvgMonthlyVisitors() {
      const total = this.get6MonthTotalVisitors();
      return Math.round(total / 6);
    },

    /**
     * 6개월 일평균 방문자 수
     */
    get6MonthAvgDailyVisitors() {
      if (!this.visitorData || Object.keys(this.visitorData).length === 0) return 0;

      // 6개월 범위 계산 (기준일 사용)
      const baseDate = this.getSimpleReportBaseDate();
      const endDate = new Date(baseDate);
      const startDate = new Date(baseDate.getFullYear(), baseDate.getMonth() - 5, 1);
      startDate.setHours(12, 0, 0, 0);
      endDate.setHours(12, 0, 0, 0);
      const startStr = this.formatDateYYYYMMDD(startDate);
      const endStr = this.formatDateYYYYMMDD(endDate);

      let total = 0;
      let days = 0;

      Object.entries(this.visitorData).forEach(([date, count]) => {
        if (date >= startStr && date <= endStr) {
          total += count;
          days++;
        }
      });

      if (days === 0) return 0;
      return Math.round(total / days);
    },

    /**
     * 6개월 성장률 계산 (첫달 대비 마지막달)
     */
    get6MonthGrowthRate() {
      const data = this.get6MonthGrowthData();
      const firstMonth = data[0]?.count || 0;
      const lastMonth = data[data.length - 1]?.count || 0;

      if (firstMonth === 0) {
        if (lastMonth > 0) return "↑ 신규";
        return "-";
      }

      const growthRate = ((lastMonth - firstMonth) / firstMonth) * 100;

      if (growthRate > 0) {
        return `↑ ${growthRate.toFixed(1)}%`;
      } else if (growthRate < 0) {
        return `↓ ${Math.abs(growthRate).toFixed(1)}%`;
      } else {
        return "→ 0%";
      }
    },

    /**
     * 성과 등급 평가
     */
    getSimpleGrade() {
      const ctr = parseFloat(this.getSimpleCTR());
      const clicks = this.getSimple3MonthClicks();

      if (clicks === 0) {
        return {
          icon: "📭",
          grade: "-",
          message: "클릭 데이터가 아직 없습니다.",
        };
      }

      if (ctr >= 5) {
        return {
          icon: "🏆",
          grade: "S",
          message: "매우 우수한 광고 성과입니다! 클릭률이 상위권에 속합니다.",
        };
      } else if (ctr >= 3) {
        return {
          icon: "🥇",
          grade: "A",
          message: "우수한 광고 성과입니다. 사용자 관심도가 높습니다.",
        };
      } else if (ctr >= 1.5) {
        return {
          icon: "🥈",
          grade: "B",
          message: "양호한 광고 성과입니다. 평균 수준의 클릭률을 보입니다.",
        };
      } else if (ctr >= 0.5) {
        return {
          icon: "🥉",
          grade: "C",
          message: "보통 수준의 성과입니다. 광고 개선을 고려해 보세요.",
        };
      } else {
        return {
          icon: "📉",
          grade: "D",
          message: "클릭률이 낮습니다. 광고 문구나 이미지 개선이 필요합니다.",
        };
      }
    },

    // CTR(Click-Through Rate) 계산
    calculateCTR(ad) {
      const impressions = ad.impressionCount || 0;
      const clicks = ad.clickCount || 0;

      if (impressions === 0) return 0;

      const ctr = (clicks / impressions) * 100;
      return ctr.toFixed(2);
    },
  },
};
</script>

<style module>
/* ========================================
   반응형 디자인 (최대 1024px)
   - Medium: 481px ~ (기본 스타일)
   - Small: ~ 480px
   ======================================== */

.container {
  width: 100%;
  max-width: 1024px;
  margin: 0 auto;
  padding: 0 15px;
  min-height: 100vh;
}

.header {
  text-align: center;
  padding: 15px;
  border-radius: 12px;
}

.title {
  font-size: 1.5rem;
  color: #333;
  margin: 0 0 8px 0;
  font-weight: bold;
}

.subtitle {
  font-size: 0.85rem;
  color: #666;
  margin: 0;
}

.error {
  text-align: center;
  padding: 60px 20px;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.retryButton {
  background: #3498db;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 1rem;
  margin-top: 15px;
  transition: background 0.3s;
}

.retryButton:hover {
  background: #2980b9;
}

/* 광고 타입 탭 */
.adTypeTabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e9ecef;
  overflow-x: auto;
}

.adTypeButton {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: none;
  border: none;
  border-radius: 0;
  border-bottom: 3px solid transparent;
  font-size: 0.85rem;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  margin-bottom: -2px;
}

.adTypeButton:hover {
  color: #007bff;
  background: rgba(0, 123, 255, 0.05);
}

.adTypeButton.activeType {
  color: #007bff;
  font-weight: bold;
  border-bottom-color: #007bff;
}

.typeIcon {
  font-size: 1.2rem;
}

.badge {
  background: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
}

.adTypeButton.activeType .badge {
  background: #0056b3;
}

/* 광고 섹션 */
.adSection {
  background: white;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.sectionHeader {
  margin-bottom: 15px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e9ecef;
}

.sectionHeaderContent {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.sectionTitle {
  font-size: 1.2rem;
  color: #333;
  margin: 0 0 8px 0;
  font-weight: bold;
}

.sectionDescription {
  font-size: 0.8rem;
  color: #666;
  margin: 0;
}

.addButton {
  padding: 10px 20px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.addButton:hover {
  background: #218838;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
}

/* 광고 리스트 */
.adList {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.adCard {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 10px;
  border: 1px solid #e9ecef;
  transition: all 0.3s ease;
}

.adCard:hover {
  border-color: #007bff;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.1);
}

.adIndex {
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  background: #007bff;
  color: white;
  border-radius: 50%;
  font-weight: bold;
  font-size: 0.8rem;
  z-index: 1;
}

.adPreview {
  width: 100%;
  height: 290px;
  border-radius: 8px;
  overflow: hidden;
  background: #e9ecef;
}

.adImage {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.noImage {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 0.9rem;
}

.adInfo {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.adTitleRow {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.adTitle {
  font-size: 0.95rem;
  font-weight: bold;
  color: #333;
  margin: 0;
  word-break: break-word;
}

.bannerSource {
  padding: 4px 10px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #495057;
}

.adDetails {
  display: flex;
  gap: 12px;
  align-items: center;
}

.adDate,
.adPlace {
  font-size: 0.85rem;
  color: #666;
  padding: 3px 8px;
  background: white;
  border-radius: 6px;
}

.adLink {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #666;
  background: white;
  padding: 6px 10px;
  border-radius: 6px;
  max-width: 100%;
  overflow: hidden;
}

.adLink a {
  color: #007bff;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.adLink a:hover {
  text-decoration: underline;
}

.adMeta {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-wrap: wrap;
}

.adType {
  padding: 2px 8px;
  background: #66bdff;
  color: white;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 500;
}

.adStatus {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.statusActive {
  background: #28a745;
  color: white;
}

.statusInactive {
  background: #6c757d;
  color: white;
}

.impressionCount {
  padding: 3px 10px;
  background: #66bdff;
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.clickCount {
  padding: 3px 10px;
  background: #17a2b8;
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.ctr {
  padding: 3px 10px;
  background: #28a745;
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.priorityBadge {
  padding: 3px 10px;
  background: #6f42c1;
  color: white;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

/* 광고 기간 표시 스타일 */
.adPeriod {
  margin-top: 12px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.periodItem {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  color: #495057;
}

.periodLabel {
  font-weight: 600;
  color: #6c757d;
}

.periodStatus {
  margin-left: auto;
}

.periodBadge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.periodActive {
  background: #28a745;
  color: white;
}

.periodInactive {
  background: #dc3545;
  color: white;
}

/* 클릭 통계 스타일 */
.clickStats {
  margin-top: 12px;
  width: 100%;
}

.statsToggle {
  width: 100%;
  padding: 9px 13px;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.statsToggle:hover {
  background: #e9ecef;
  border-color: #007bff;
  color: #007bff;
}

.statsContent {
  margin-top: 10px;
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
}

.statsHeader {
  padding: 11px 14px;
  background: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  font-weight: 600;
  color: #495057;
  font-size: 0.85rem;
}

.monthFilter {
  display: flex;
  gap: 7px;
  padding: 10px 14px;
  background: white;
  border-bottom: 1px solid #f1f3f5;
  overflow-x: auto;
  flex-wrap: wrap;
}

.monthButton {
  padding: 5px 12px;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.monthButton:hover {
  background: #e9ecef;
  border-color: #17a2b8;
  color: #17a2b8;
}

.monthButton.activeMonth {
  background: #17a2b8;
  border-color: #17a2b8;
  color: white;
  font-weight: 600;
}

.chartWrapper {
  padding: 18px 14px;
  background: white;
}

.clickChart {
  height: 230px;
}

.adActions {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
  width: 100%;
  margin-top: 4px;
  padding-top: 10px;
  border-top: 1px solid #e9ecef;
}

.priorityControls {
  display: flex;
  gap: 4px;
  justify-content: stretch;
}

.priorityButton {
  flex: 1;
  padding: 7px 10px;
  border: 2px solid #6f42c1;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  color: #6f42c1;
}

.priorityButton:hover:not(:disabled) {
  background: #6f42c1;
  color: white;
  transform: translateY(-2px);
}

.priorityButton:disabled {
  background: #f8f9fa;
  color: #adb5bd;
  border-color: #dee2e6;
  cursor: not-allowed;
  transform: none;
}

.priorityUp {
  border-color: #28a745;
  color: #28a745;
}

.priorityUp:hover:not(:disabled) {
  background: #28a745;
  color: white;
}

.priorityDown {
  border-color: #dc3545;
  color: #dc3545;
}

.priorityDown:hover:not(:disabled) {
  background: #dc3545;
  color: white;
}

.actionButton {
  padding: 9px 16px;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.activateButton {
  background: #28a745;
  color: white;
}

.activateButton:hover:not(:disabled) {
  background: #218838;
}

.deactivateButton {
  background: #dc3545;
  color: white;
}

.deactivateButton:hover:not(:disabled) {
  background: #c82333;
}

.viewButton {
  background: #17a2b8;
  color: white;
}

.viewButton:hover {
  background: #138496;
}

.editButton {
  background: #ffc107;
  color: #212529;
}

.editButton:hover {
  background: #e0a800;
}

.deleteButton {
  background: #6c757d;
  color: white;
}

.deleteButton:hover:not(:disabled) {
  background: #5a6268;
}

.reportButton {
  background: #6f42c1;
  color: white;
}

.reportButton:hover {
  background: #5a32a3;
}

.simpleReportButton {
  background: #20c997;
  color: white;
}

.simpleReportButton:hover {
  background: #1aa179;
}

.invoiceButton {
  background: #f97316;
  color: white;
}

.invoiceButton:hover {
  background: #ea580c;
}

.actionButton:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.emptyState {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
  font-size: 1.1rem;
}

/* 준비 중 상태 */
.comingSoon {
  text-align: center;
  padding: 80px 20px;
}

.comingSoonIcon {
  font-size: 5rem;
  margin-bottom: 20px;
}

.comingSoonTitle {
  font-size: 2rem;
  color: #333;
  margin: 0 0 15px 0;
}

.comingSoonText {
  font-size: 1.1rem;
  color: #666;
  margin: 0 0 10px 0;
}

.comingSoonSubtext {
  font-size: 0.95rem;
  color: #adb5bd;
  margin: 0;
}

/* ========================================
   Small (~480px)
   ======================================== */
@media (max-width: 480px) {
  .container {
    width: 100%;
    padding: 0 10px 10px 10px;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .header {
    padding: 15px 10px;
    border-radius: 10px;
  }

  .title {
    font-size: 1.5rem;
  }

  .subtitle {
    font-size: 0.85rem;
  }

  .error {
    padding: 40px 15px;
    border-radius: 10px;
  }

  .retryButton {
    padding: 8px 16px;
    font-size: 0.9rem;
  }

  .adTypeTabs {
    gap: 6px;
    margin-bottom: 20px;
    overflow-x: auto;
    padding-bottom: 5px;
  }

  .adTypeButton {
    flex-shrink: 0;
    padding: 8px 12px;
    font-size: 0.85rem;
  }

  .typeIcon {
    font-size: 1rem;
  }

  .badge {
    font-size: 0.7rem;
    padding: 2px 5px;
  }

  .adSection {
    padding: 15px;
    border-radius: 10px;
  }

  .sectionHeader {
    margin-bottom: 18px;
    padding-bottom: 12px;
  }

  .sectionHeaderContent {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .sectionTitle {
    font-size: 1.2rem;
    margin-bottom: 6px;
  }

  .sectionDescription {
    font-size: 0.8rem;
  }

  .addButton {
    width: 100%;
    padding: 10px 16px;
    font-size: 0.9rem;
  }

  .adList {
    gap: 12px;
  }

  .adCard {
    flex-direction: column;
    gap: 10px;
    padding: 10px;
    border-radius: 8px;
  }

  .adIndex {
    position: absolute;
    top: 8px;
    left: 8px;
    width: 24px;
    height: 24px;
    font-size: 0.75rem;
  }

  .adPreview {
    width: 100%;
    height: 90px;
    border-radius: 6px;
  }

  .adInfo {
    gap: 5px;
    width: 100%;
  }

  .adTitleRow {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .adTitle {
    font-size: 0.9rem;
    word-break: break-word;
  }

  .bannerSource {
    font-size: 0.7rem;
    padding: 3px 8px;
  }

  .adDetails {
    gap: 6px;
    flex-wrap: wrap;
  }

  .adDate,
  .adPlace {
    font-size: 0.75rem;
    padding: 3px 8px;
    border-radius: 5px;
  }

  .adLink {
    font-size: 0.75rem;
    padding: 5px 8px;
    border-radius: 5px;
  }

  .adMeta {
    gap: 6px;
    flex-wrap: wrap;
  }

  .adType,
  .adStatus,
  .impressionCount,
  .clickCount,
  .ctr,
  .priorityBadge {
    font-size: 0.75rem;
    padding: 3px 8px;
    border-radius: 10px;
  }

  .adPeriod {
    padding: 8px;
    gap: 8px;
    margin-top: 8px;
    border-radius: 6px;
    flex-direction: column;
    align-items: flex-start;
  }

  .periodItem {
    font-size: 0.75rem;
    gap: 4px;
  }

  .periodLabel {
    font-size: 0.7rem;
  }

  .periodStatus {
    width: 100%;
    margin-left: 0;
  }

  .periodBadge {
    font-size: 0.75rem;
    padding: 3px 10px;
    display: block;
    text-align: center;
  }

  .clickStats {
    margin-top: 10px;
  }

  .statsToggle {
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 6px;
  }

  .statsContent {
    border-radius: 6px;
  }

  .statsHeader {
    padding: 10px 12px;
    font-size: 0.8rem;
  }

  .monthFilter {
    padding: 8px 10px;
    gap: 5px;
  }

  .monthButton {
    padding: 4px 10px;
    font-size: 0.75rem;
    border-radius: 15px;
  }

  .chartWrapper {
    padding: 12px 10px;
  }

  .clickChart {
    height: 180px;
  }

  .adActions {
    width: 100%;
    flex-direction: row;
    gap: 6px;
  }

  .priorityControls {
    gap: 4px;
  }

  .priorityButton {
    padding: 5px 8px;
    font-size: 0.85rem;
    border-radius: 4px;
  }

  .actionButton {
    flex: 1;
    padding: 8px 10px;
    font-size: 0.8rem;
    border-radius: 5px;
  }

  .emptyState {
    padding: 40px 15px;
    font-size: 0.95rem;
  }

  .comingSoon {
    padding: 50px 15px;
  }

  .comingSoonIcon {
    font-size: 3rem;
    margin-bottom: 15px;
  }

  .comingSoonTitle {
    font-size: 1.4rem;
  }

  .comingSoonText {
    font-size: 0.9rem;
  }

  .comingSoonSubtext {
    font-size: 0.85rem;
  }

  .adPeriodSection {
    padding: 12px;
    border-radius: 10px;
  }

  .periodSectionHeader {
    margin-bottom: 12px;
  }

  .periodSectionTitle {
    font-size: 0.95rem;
    margin-bottom: 6px;
  }

  .periodSectionDescription {
    font-size: 0.8rem;
  }
}

/* 모달 스타일 */
.modalContent {
  padding: 18px 0;
}

.formGroup {
  margin-bottom: 18px;
}

.formLabel {
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.formInput {
  width: 100%;
  padding: 9px 11px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.3s;
}

.formInput:focus {
  outline: none;
  border-color: #007bff;
}

.formHint {
  display: block;
  margin-top: 6px;
  font-size: 0.75rem;
  color: #6c757d;
}

.formRow {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
}

.formCheckbox {
  margin-right: 8px;
}

.radioGroup {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.radioLabel {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.radioLabel:hover {
  border-color: #007bff;
  background: rgba(0, 123, 255, 0.05);
}

.radioLabel:has(input:checked) {
  border-color: #007bff;
  background: rgba(0, 123, 255, 0.1);
}

.formRadio {
  margin: 0;
  margin-top: 3px;
  cursor: pointer;
  flex-shrink: 0;
}

.radioText {
  flex: 1;
  font-size: 0.85rem;
  line-height: 1.5;
}

.radioText strong {
  display: block;
  color: #333;
  margin-bottom: 4px;
}

/* 광고 기간 설정 섹션 스타일 */
.adPeriodSection {
  padding: 18px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px solid #e9ecef;
  margin-bottom: 20px;
}

.periodSectionHeader {
  margin-bottom: 15px;
}

.periodSectionTitle {
  font-size: 1rem;
  color: #333;
  margin: 0 0 8px 0;
  font-weight: bold;
}

.periodSectionDescription {
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

.modalFooter {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 18px;
  border-top: 1px solid #e9ecef;
}

.modalButton {
  padding: 9px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancelButton {
  background: #6c757d;
  color: white;
}

.cancelButton:hover {
  background: #5a6268;
}

.saveButton {
  background: #007bff;
  color: white;
}

.saveButton:hover:not(:disabled) {
  background: #0056b3;
}

.saveButton:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

/* 이미지 업로드 스타일 */
.uploadArea {
  margin-bottom: 15px;
}

.fileInput {
  display: none;
}

.uploadButton {
  width: 100%;
  padding: 10px 18px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.uploadButton:hover:not(:disabled) {
  background: #218838;
}

.uploadButton:disabled {
  background: #6c757d;
  cursor: not-allowed;
}

.uploadProgress {
  margin-top: 12px;
}

.progressBar {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progressFill {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progressText {
  display: block;
  text-align: center;
  font-size: 0.85rem;
  color: #666;
  font-weight: 600;
}

.imagePreviewContainer {
  position: relative;
  margin-bottom: 15px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid #e9ecef;
}

.imagePreview {
  width: 100%;
  max-height: 250px;
  object-fit: contain;
  display: block;
  background: #f8f9fa;
}

.clearImageButton {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 7px 14px;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}

.clearImageButton:hover {
  background: rgba(200, 35, 51, 0.95);
  transform: scale(1.05);
}

.orDivider {
  text-align: center;
  margin: 15px 0;
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
  position: relative;
}

.orDivider::before,
.orDivider::after {
  content: "";
  position: absolute;
  top: 50%;
  width: 42%;
  height: 1px;
  background: #dee2e6;
}

.orDivider::before {
  left: 0;
}

.orDivider::after {
  right: 0;
}

/* ========================================
   Small - 모달 스타일 (~480px)
   ======================================== */
@media (max-width: 480px) {
  .modalContent {
    padding: 12px 0;
  }

  .formGroup {
    margin-bottom: 15px;
  }

  .formLabel {
    font-size: 0.85rem;
    margin-bottom: 5px;
  }

  .formInput {
    padding: 8px 10px;
    font-size: 0.85rem;
    border-radius: 6px;
  }

  .formHint {
    font-size: 0.7rem;
    margin-top: 4px;
  }

  .formRow {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .formCheckbox {
    margin-right: 6px;
  }

  .radioGroup {
    gap: 8px;
  }

  .radioLabel {
    padding: 10px;
    border-radius: 6px;
  }

  .radioText {
    font-size: 0.8rem;
  }

  .radioText strong {
    margin-bottom: 2px;
  }

  .uploadButton {
    padding: 9px 14px;
    font-size: 0.85rem;
    border-radius: 6px;
  }

  .uploadProgress {
    margin-top: 10px;
  }

  .progressBar {
    height: 6px;
    margin-bottom: 6px;
  }

  .progressText {
    font-size: 0.8rem;
  }

  .imagePreviewContainer {
    border-radius: 6px;
    margin-bottom: 12px;
  }

  .imagePreview {
    max-height: 180px;
  }

  .clearImageButton {
    top: 8px;
    right: 8px;
    padding: 6px 10px;
    font-size: 0.75rem;
    border-radius: 4px;
  }

  .orDivider {
    margin: 12px 0;
    font-size: 0.85rem;
  }

  .modalFooter {
    padding-top: 15px;
    gap: 8px;
  }

  .modalButton {
    padding: 9px 18px;
    font-size: 0.85rem;
    border-radius: 6px;
  }
}

/* ========================================
   A4 문서 스타일 통계 보고서
   ======================================== */
.reportModal {
  padding: 0 !important;
}

.reportModal :global(.p-dialog-content) {
  padding: 0 !important;
  background: #e8e8e8;
}

.a4Document {
  position: relative;
  width: 100%;
  max-width: 800px;
  margin: 20px auto;
  background: white;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-radius: 2px;
  min-height: 1000px;
}

/* 문서 헤더 */
.documentHeader {
  padding: 40px 50px 30px;
  border-bottom: 3px solid #1a1a2e;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: white;
}

.documentLogo {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 3px;
  color: #00d9ff;
  margin-bottom: 15px;
}

.documentTitle {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 15px;
  letter-spacing: 1px;
}

.documentMeta {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  gap: 8px;
}

.documentDivider {
  opacity: 0.4;
}

/* 기간 선택 UI */
.periodSelector {
  padding: 20px 50px;
  border-bottom: 1px solid #e9ecef;
  background: #f8f9fa;
}

.periodLabel {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 12px;
}

.periodButtons {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.periodBtn {
  padding: 8px 16px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: white;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.periodBtn:hover {
  border-color: #66bdff;
  color: #66bdff;
}

.periodBtn.active {
  background: #66bdff;
  border-color: #66bdff;
  color: white;
}

.customPeriod {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.dateInput {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #495057;
}

.dateInput:focus {
  outline: none;
  border-color: #66bdff;
}

.dateSeparator {
  color: #adb5bd;
  font-weight: 500;
}

.periodInfo {
  padding: 10px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #495057;
}

/* 문서 본문 */
.documentBody {
  padding: 40px 50px;
}

/* 섹션 스타일 */
.docSection {
  position: relative;
  margin-bottom: 35px;
  padding-left: 50px;
}

.sectionNumber {
  position: absolute;
  left: 0;
  top: 0;
  font-size: 1.5rem;
  font-weight: 800;
  color: #e0e0e0;
  font-family: 'Courier New', monospace;
}

.docSectionTitle {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #f0f0f0;
}

/* 정보 박스 */
.docInfoBox {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 20px;
}

.docInfoRow {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 15px;
}

.docInfoRow:last-child {
  margin-bottom: 0;
}

.docInfoCell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.docLabel {
  font-size: 0.75rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.docValue {
  font-size: 0.95rem;
  color: #333;
  font-weight: 500;
}

.docStatus {
  font-weight: 600;
}

.docStatusActive {
  color: #28a745;
}

.docStatusInactive {
  color: #dc3545;
}

/* KPI 그리드 */
.kpiGrid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.kpiCard {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.kpiCard:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.kpiHighlight {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
}

.kpiHighlight .kpiLabel,
.kpiHighlight .kpiUnit {
  color: rgba(255, 255, 255, 0.85);
}

.kpiHighlight .kpiValue {
  color: white;
}

.kpiIcon {
  font-size: 1.8rem;
}

.kpiContent {
  flex: 1;
}

.kpiLabel {
  font-size: 0.75rem;
  font-weight: 600;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.kpiValue {
  font-size: 1.6rem;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;
}

.kpiUnit {
  font-size: 0.8rem;
  color: #888;
  margin-top: 2px;
}

/* 통계 비교 박스 */
.statsCompare {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.statsBox {
  background: #f8f9fa;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.statsBoxTitle {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

.statsBoxIcon {
  font-size: 1.1rem;
}

.statsItems {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.statsItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px dashed #e8e8e8;
}

.statsItem:last-child {
  border-bottom: none;
}

.statsItemLabel {
  font-size: 0.85rem;
  color: #666;
}

.statsItemValue {
  font-size: 0.9rem;
  font-weight: 600;
  color: #1a1a2e;
}

/* 차트 래퍼 */
.docChartWrapper {
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 25px;
}

.docChart {
  height: 280px;
}

/* 테이블 스타일 */
.docTableWrapper {
  overflow-x: auto;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
}

.docTable {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
}

.docTable thead {
  background: #1a1a2e;
  color: white;
}

.docTable th {
  padding: 12px 15px;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.docTable td {
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}

.docTable tbody tr:nth-child(even) {
  background: #fafafa;
}

.docTable tbody tr:hover {
  background: #f0f5ff;
}

.docTable tbody tr:last-child td {
  border-bottom: none;
}

.ctrHighlight {
  font-weight: 700;
  color: #667eea;
}

/* 데이터 없음 */
.docNoData {
  text-align: center;
  padding: 60px 20px;
  background: #fafafa;
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
}

.docNoDataIcon {
  font-size: 3rem;
  margin-bottom: 15px;
  opacity: 0.5;
}

.docNoDataText {
  font-size: 1rem;
  color: #666;
  margin: 0 0 8px 0;
  font-weight: 500;
}

.docNoDataSub {
  font-size: 0.85rem;
  color: #999;
  margin: 0;
}

/* 문서 푸터 */
.documentFooter {
  padding: 30px 50px 40px;
  margin-top: auto;
}

.footerLine {
  height: 2px;
  background: linear-gradient(90deg, #1a1a2e, #667eea, #1a1a2e);
  margin-bottom: 20px;
}

.footerContent {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #888;
}

.footerPage {
  font-weight: 500;
}

/* 닫기 버튼 */
.docCloseButton {
  position: absolute;
  top: 15px;
  right: 15px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
}

.docCloseButton:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* ========================================
   Small - A4 문서 스타일 (~480px)
   ======================================== */
@media (max-width: 480px) {
  .a4Document {
    margin: 10px;
    min-height: auto;
  }

  .documentHeader {
    padding: 25px 20px 20px;
  }

  .documentLogo {
    font-size: 0.75rem;
    letter-spacing: 2px;
  }

  .documentTitle {
    font-size: 1.3rem;
  }

  .documentMeta {
    font-size: 0.75rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }

  .documentDivider {
    display: none;
  }

  .periodSelector {
    padding: 15px 20px;
  }

  .periodLabel {
    font-size: 0.8rem;
    margin-bottom: 10px;
  }

  .periodButtons {
    gap: 6px;
  }

  .periodBtn {
    padding: 6px 12px;
    font-size: 0.75rem;
  }

  .customPeriod {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .dateInput {
    width: 100%;
    font-size: 0.8rem;
  }

  .periodInfo {
    font-size: 0.75rem;
    padding: 8px 12px;
  }

  .documentBody {
    padding: 25px 20px;
  }

  .docSection {
    padding-left: 0;
    margin-bottom: 25px;
  }

  .sectionNumber {
    position: static;
    font-size: 0.9rem;
    margin-bottom: 5px;
    color: #667eea;
  }

  .docSectionTitle {
    font-size: 1rem;
    margin-bottom: 15px;
  }

  .docInfoBox {
    padding: 15px;
  }

  .docInfoRow {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .docLabel {
    font-size: 0.7rem;
  }

  .docValue {
    font-size: 0.9rem;
  }

  .kpiGrid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .kpiCard {
    padding: 15px;
  }

  .kpiIcon {
    font-size: 1.5rem;
  }

  .kpiValue {
    font-size: 1.4rem;
  }

  .statsCompare {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .statsBox {
    padding: 15px;
  }

  .statsBoxTitle {
    font-size: 0.85rem;
  }

  .statsItem {
    padding: 6px 0;
  }

  .statsItemLabel {
    font-size: 0.8rem;
  }

  .statsItemValue {
    font-size: 0.85rem;
  }

  .docChartWrapper {
    padding: 15px;
  }

  .docChart {
    height: 220px;
  }

  .docTable th,
  .docTable td {
    padding: 10px 12px;
    font-size: 0.8rem;
  }

  .docTable th {
    font-size: 0.7rem;
  }

  .docNoData {
    padding: 40px 15px;
  }

  .docNoDataIcon {
    font-size: 2.5rem;
  }

  .docNoDataText {
    font-size: 0.9rem;
  }

  .docNoDataSub {
    font-size: 0.8rem;
  }

  .documentFooter {
    padding: 20px;
  }

  .footerContent {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .docCloseButton {
    top: 10px;
    right: 10px;
    padding: 8px 15px;
    font-size: 0.8rem;
  }
}

/* ========================================
   간이 광고 성과 보고서 스타일
   ======================================== */
.simpleReportModal {
  padding: 0 !important;
}

.simpleReportModal :global(.p-dialog-content) {
  padding: 0 !important;
  background: transparent;
}

.simpleReport {
  background: linear-gradient(180deg, #f0f9ff 0%, #ffffff 30%);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.12);
}

/* 간이 보고서 헤더 */
.simpleHeader {
  text-align: center;
  padding: 30px 25px 25px;
  background: linear-gradient(135deg, #20c997 0%, #0ca678 100%);
  color: white;
}

.simpleHeaderBadge {
  display: inline-block;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 12px;
}

.simpleTitle {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0 0 6px 0;
}

.simpleSubtitle {
  font-size: 0.9rem;
  opacity: 0.9;
  margin: 0;
}

/* 광고 정보 */
.simpleAdInfo {
  padding: 20px 25px;
  background: white;
  border-bottom: 1px solid #e9ecef;
  text-align: center;
}

.simpleAdName {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}

.simpleAdMeta {
  font-size: 0.85rem;
  color: #666;
}

/* 핵심 지표 */
.simpleMetrics {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  padding: 25px;
  background: #f8f9fa;
}

.simpleMetricCard {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.simpleMetricHighlight {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.simpleMetricHighlight .simpleMetricLabel {
  color: rgba(255, 255, 255, 0.85);
}

.simpleMetricHighlight .simpleMetricValue {
  color: white;
}

.simpleMetricIcon {
  font-size: 1.8rem;
}

.simpleMetricContent {
  flex: 1;
}

.simpleMetricLabel {
  font-size: 0.75rem;
  color: #888;
  font-weight: 600;
  margin-bottom: 4px;
}

.simpleMetricValue {
  font-size: 1.3rem;
  font-weight: 800;
  color: #333;
}

/* 참고 자료 */
.simpleReference {
  padding: 25px;
  background: white;
}

.simpleRefTitle {
  font-size: 0.95rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 15px 0;
}

.simpleRefContent {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
}

.simpleRefItem {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #e0e0e0;
}

.simpleRefItem:last-child {
  border-bottom: none;
}

.simpleRefLabel {
  font-size: 0.85rem;
  color: #666;
}

.simpleRefValue {
  font-size: 0.9rem;
  font-weight: 700;
  color: #333;
}

.simpleRefNote {
  margin: 12px 0 0 0;
  font-size: 0.75rem;
  color: #999;
  line-height: 1.5;
}

/* 6개월 성장추이 */
.simpleGrowth {
  padding: 25px;
  background: white;
  border-top: 1px solid #e9ecef;
}

.simpleGrowthTitle {
  font-size: 0.95rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 15px 0;
}

.simpleGrowthChartWrapper {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  margin-bottom: 20px;
}

.simpleGrowthChart {
  height: 180px;
}

.simpleGrowthStats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
}

.growthStatItem {
  text-align: center;
  padding: 10px;
  background: white;
  border-radius: 8px;
}

.growthStatHighlight {
  background: linear-gradient(135deg, #20c997 0%, #0ca678 100%);
  color: white;
}

.growthStatHighlight .growthStatLabel {
  color: rgba(255, 255, 255, 0.85);
}

.growthStatHighlight .growthStatValue {
  color: white;
}

.growthStatLabel {
  display: block;
  font-size: 0.7rem;
  color: #888;
  font-weight: 600;
  margin-bottom: 4px;
}

.growthStatValue {
  font-size: 1rem;
  font-weight: 800;
  color: #333;
}

/* 요약 평가 */
.simpleSummary {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px 25px;
  background: #f0f9ff;
  border-top: 1px solid #e0e0e0;
}

.simpleSummaryIcon {
  font-size: 2.5rem;
}

.simpleSummaryContent {
  flex: 1;
}

.simpleSummaryGrade {
  font-size: 0.9rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}

.simpleSummaryText {
  font-size: 0.85rem;
  color: #666;
  line-height: 1.5;
}

/* 푸터 */
.simpleFooter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 25px;
  background: white;
  border-top: 1px solid #e9ecef;
}

.simpleFooter span {
  font-size: 0.8rem;
  color: #999;
}

.simpleFooterControls {
  display: flex;
  align-items: center;
  gap: 15px;
}

.monthNavigator {
  display: flex;
  gap: 8px;
}

.monthNavBtn {
  padding: 8px 15px;
  background: white;
  color: #495057;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.monthNavBtn:hover:not(:disabled) {
  background: #f8f9fa;
  border-color: #20c997;
  color: #20c997;
}

.monthNavBtn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.simpleCloseBtn {
  padding: 10px 25px;
  background: #6c757d;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
}

.simpleCloseBtn:hover {
  background: #5a6268;
}

/* ========================================
  Invoice 미리보기 스타일
  ======================================== */
.invoiceModal {
  padding: 0 !important;
}

.invoiceModal :global(.p-dialog-content) {
  padding: 0 !important;
  background: #f3f4f6;
}

.invoiceModalContent {
  padding: 24px;
}

.invoicePreviewLabel {
  margin-bottom: 14px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #374151;
}

.invoicePeriodEditor {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.invoicePeriodField {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.invoicePeriodLabel {
  font-size: 0.82rem;
  font-weight: 700;
  color: #374151;
}

.invoicePeriodInput {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: white;
  color: #111827;
  font-size: 0.92rem;
}

.invoicePeriodInput:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12);
}

.invoiceDocument {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.12);
}

.invoiceHeader {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 32px;
  background: linear-gradient(135deg, #111827 0%, #1f2937 55%, #374151 100%);
  color: white;
}

.invoiceEyebrow {
  margin-bottom: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 2px;
  color: #fbbf24;
}

.invoiceTitle {
  margin: 0;
  font-size: 2rem;
  font-weight: 800;
  letter-spacing: 0.04em;
}

.invoiceSubtitle {
  margin: 8px 0 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.9rem;
}

.invoiceMeta {
  min-width: 220px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.invoiceMetaRow {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.invoiceMetaLabel {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.65);
}

.invoiceMetaValue {
  font-size: 0.95rem;
  font-weight: 600;
  word-break: break-word;
}

.invoiceSection {
  padding: 24px 32px 0;
}

.invoiceSectionHeading {
  margin-bottom: 12px;
  font-size: 0.95rem;
  font-weight: 800;
  color: #111827;
}

.invoiceBusinessTable {
  border: 1px solid #d1d5db;
  border-radius: 16px;
  overflow: hidden;
  background: white;
}

.invoiceBusinessRow {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.invoiceBusinessRow + .invoiceBusinessRow {
  border-top: 1px solid #e5e7eb;
}

.invoiceBusinessCell {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 18px;
  min-height: 84px;
}

.invoiceBusinessCell + .invoiceBusinessCell {
  border-left: 1px solid #e5e7eb;
}

.invoiceBusinessLabel {
  font-size: 0.78rem;
  font-weight: 700;
  color: #6b7280;
}

.invoiceBusinessValue {
  font-size: 0.98rem;
  font-weight: 600;
  color: #111827;
  line-height: 1.5;
  word-break: break-word;
}

.invoiceLineItems {
  border: 1px solid #d1d5db;
  border-radius: 16px;
  overflow: hidden;
  background: white;
}

.invoiceLineRow {
  display: grid;
  grid-template-columns: 1fr 1.4fr 1.7fr 0.9fr;
  gap: 16px;
  align-items: center;
  padding: 16px 18px;
  font-size: 0.92rem;
  color: #111827;
}

.invoiceLineHeader {
  background: #f3f4f6;
  font-size: 0.8rem;
  font-weight: 800;
  color: #4b5563;
}

.invoiceLineRow + .invoiceLineRow {
  border-top: 1px solid #e5e7eb;
}

.invoiceAmountSummary {
  padding: 18px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.invoiceAmountRow {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 7px 0;
  font-size: 0.95rem;
  color: #374151;
}

.invoiceAmountTotal {
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid #d1d5db;
  font-size: 1.05rem;
  color: #111827;
}

.invoiceAccountBox {
  display: grid;
  gap: 10px;
  padding: 18px;
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: linear-gradient(180deg, #fff7ed 0%, #ffffff 100%);
}

.invoiceAccountRow {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid #fed7aa;
}

.invoiceAccountLabel {
  font-size: 0.82rem;
  font-weight: 700;
  color: #9a3412;
}

.invoiceAccountValue {
  font-size: 0.95rem;
  font-weight: 700;
  color: #111827;
  text-align: right;
  word-break: break-word;
}

.invoicePreviewGrid {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
  gap: 16px;
}

.invoiceNoteText {
  margin: 0;
  color: #4b5563;
  font-size: 0.92rem;
  line-height: 1.6;
}

.invoiceFooter {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
  padding: 18px 32px 24px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 0.82rem;
}

.invoiceActions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
}

.invoiceActionButton {
  padding: 11px 18px;
  border: none;
  border-radius: 10px;
  font-size: 0.92rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.invoiceCancelButton {
  background: #e5e7eb;
  color: #374151;
}

.invoiceCancelButton:hover {
  background: #d1d5db;
}

.invoiceSaveButton {
  background: #f97316;
  color: white;
}

.invoiceSaveButton:hover:not(:disabled) {
  background: #ea580c;
}

.invoiceSaveButton:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* Small - 간이 보고서 스타일 (~480px) */
@media (max-width: 480px) {
  .simpleReport {
    border-radius: 12px;
  }

  .simpleHeader {
    padding: 25px 20px 20px;
  }

  .simpleHeaderBadge {
    font-size: 0.65rem;
    padding: 3px 10px;
  }

  .simpleTitle {
    font-size: 1.2rem;
  }

  .simpleSubtitle {
    font-size: 0.85rem;
  }

  .simpleAdInfo {
    padding: 15px 20px;
  }

  .simpleAdName {
    font-size: 1rem;
  }

  .simpleAdMeta {
    font-size: 0.8rem;
  }

  .simpleMetrics {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 20px;
  }

  .simpleMetricCard {
    padding: 15px;
  }

  .simpleMetricIcon {
    font-size: 1.5rem;
  }

  .simpleMetricValue {
    font-size: 1.2rem;
  }

  .simpleReference {
    padding: 20px;
  }

  .simpleRefTitle {
    font-size: 0.9rem;
  }

  .simpleRefContent {
    padding: 12px;
  }

  .simpleRefItem {
    padding: 8px 0;
  }

  .simpleRefLabel {
    font-size: 0.8rem;
  }

  .simpleRefValue {
    font-size: 0.85rem;
  }

  .simpleRefNote {
    font-size: 0.7rem;
  }

  .simpleSummary {
    padding: 15px 20px;
    gap: 12px;
  }

  .simpleSummaryIcon {
    font-size: 2rem;
  }

  .simpleSummaryGrade {
    font-size: 0.85rem;
  }

  .simpleSummaryText {
    font-size: 0.8rem;
  }

  .simpleFooter {
    padding: 12px 20px;
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .simpleFooter span {
    text-align: center;
  }

  .simpleFooterControls {
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .monthNavigator {
    width: 100%;
    justify-content: center;
  }

  .monthNavBtn {
    flex: 1;
    padding: 10px 12px;
    font-size: 0.8rem;
  }

  .simpleCloseBtn {
    padding: 10px 20px;
    font-size: 0.85rem;
    width: 100%;
  }

  .invoiceModalContent {
    padding: 14px;
  }

  .invoicePeriodEditor {
    grid-template-columns: 1fr;
  }

  .invoiceHeader {
    flex-direction: column;
    padding: 24px 20px;
  }

  .invoiceTitle {
    font-size: 1.6rem;
  }

  .invoiceMeta {
    min-width: 0;
  }

  .invoiceSection {
    padding: 18px 20px 0;
  }

  .invoiceBusinessRow,
  .invoiceLineRow,
  .invoicePreviewGrid {
    grid-template-columns: 1fr;
  }

  .invoiceBusinessCell + .invoiceBusinessCell {
    border-left: none;
    border-top: 1px solid #e5e7eb;
  }

  .invoiceLineRow {
    gap: 8px;
  }

  .invoiceAccountRow {
    flex-direction: column;
    align-items: flex-start;
  }

  .invoiceAccountValue {
    text-align: left;
  }

  .invoiceFooter {
    flex-direction: column;
    padding: 16px 20px 20px;
    text-align: center;
  }

  .invoiceActions {
    flex-direction: column;
  }

  .invoiceActionButton {
    width: 100%;
  }

  .simpleGrowth {
    padding: 20px;
  }

  .simpleGrowthTitle {
    font-size: 0.9rem;
    margin-bottom: 12px;
  }

  .simpleGrowthChartWrapper {
    padding: 12px;
    margin-bottom: 15px;
  }

  .simpleGrowthChart {
    height: 150px;
  }

  .simpleGrowthStats {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 12px;
  }

  .growthStatItem {
    padding: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .growthStatLabel {
    font-size: 0.75rem;
    margin-bottom: 0;
  }

  .growthStatValue {
    font-size: 0.70rem;
  }
}
</style>
